import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from './AuthContext';
import web2mobile from './assets/vitrine/web2mobile.jpg';

const withUploadsPath = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  return raw.startsWith('/uploads/') ? raw : `/uploads/${raw.replace(/^\/+/, '')}`;
};

const isVideoFile = (value = '', mime = '') => {
  const fileValue = String(value || '').split('?')[0].split('#')[0].toLowerCase();
  const mimeValue = String(mime || '').toLowerCase();
  return mimeValue.startsWith('video/') || /\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/i.test(fileValue);
};

const isImageFile = (value = '', mime = '') => {
  const fileValue = String(value || '').split('?')[0].split('#')[0].toLowerCase();
  const mimeValue = String(mime || '').toLowerCase();
  return mimeValue.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif|bmp|svg)$/i.test(fileValue);
};

const safeParseSteps = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const extractRowsFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload?.rows,
    payload?.items,
    payload?.data,
    payload?.results,
    payload?.media,
    payload?.medias,
    payload?.list,
  ];
  for (const entry of candidates) {
    if (Array.isArray(entry)) return entry;
  }
  return [];
};

const mediaKindOf = (media) => {
  const playback = buildPlaybackSource(media);
  const preview = buildPreviewSource(media);
  const mime = media?.mime_type || media?.mimeType || media?.type || media?.file_type || media?.content_type || media?.kind || '';
  return isVideoFile(playback || preview, mime) ? 'video' : 'image';
};

const normalizeUser = (media) => String(
  media?.contributor || media?.created_by || media?.username || media?.user_id || ''
).toLowerCase();

const readMediaValue = (value) => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const picked = readMediaValue(item);
      if (picked) return picked;
    }
    return '';
  }
  if (value && typeof value === 'object') {
    return readMediaValue(
      value?.url ||
      value?.path ||
      value?.src ||
      value?.value ||
      value?.file_path ||
      value?.filename ||
      value?.file_name ||
      value?.name ||
      ''
    );
  }
  return '';
};

const pickMediaField = (media, keys) => {
  for (const key of keys) {
    const picked = readMediaValue(media?.[key]);
    if (picked) return picked;
  }
  return '';
};

const buildPlaybackSource = (media) => pickMediaField(media, [
  'file_path',
  'path',
  'url',
  'media_url',
  'video_url',
  'source_url',
  'fileUrl',
  'stream_url',
  'download_url',
  'public_url',
  'secure_url',
  'signed_url',
  'original_url',
  'asset_url',
  'src',
  'storage_path',
  'filename',
  'file_name',
  'name',
]);

const buildPreviewSource = (media) => pickMediaField(media, [
  'thumb_path',
  'thumbnail_url',
  'preview_url',
  'poster_url',
  'cover_url',
  'thumb',
  'thumbnail',
  'preview',
  'poster',
  'cover',
  'image',
  'image_url',
  'screenshot_url',
]) || buildPlaybackSource(media);

const buildMediaSource = (media) => (
  buildPlaybackSource(media) || buildPreviewSource(media)
);

const mediaUniqueKey = (media) => {
  if (!media) return 'unknown-media';
  const recordId = media?.id ?? media?.linked_media_id ?? media?.media_id ?? media?.mediaId ?? null;
  if (recordId != null) return `id:${recordId}`;
  const src = String(buildMediaSource(media) || '').trim();
  if (src) return `file:${src}`;
  return [media?.title || '', media?.created_at || '', media?.location || ''].join('|').toLowerCase();
};

const statusMeta = (media) => {
  if (!media) return { label: 'In review', tone: 'muted', icon: 'bi-hourglass-split' };
  const raw = String(media?.status || media?.state || '').toLowerCase();
  if (Number(media?.selected) === 1 || raw.includes('final') || raw.includes('used')) {
    return { label: 'Used in final', tone: 'success', icon: 'bi-check-circle-fill' };
  }
  if (Number(media?.approved) === 1 || raw.includes('approved') || raw.includes('retain') || raw.includes('selected')) {
    return { label: 'Approved', tone: 'success', icon: 'bi-check2-circle' };
  }
  if (media?.step_id != null) {
    return { label: 'Assigned', tone: 'info', icon: 'bi-link-45deg' };
  }
  return { label: 'In review', tone: 'muted', icon: 'bi-hourglass-split' };
};

const formatWhen = (value) => {
  if (!value) return 'Just now';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Recently';
  return d.toLocaleString();
};

const avatarFor = (usersByUsername, username) => (
  usersByUsername?.[username]?.avatar_url ||
  `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(username || 'W')}`
);

const computeCompatibility = (media) => {
  if (!media) return 72;
  let score = 62;
  if (media?.step_id != null) score += 10;
  if (Number(media?.selected) === 1) score += 12;
  if ((media?.description || '').trim()) score += 6;
  if (isVideoFile(media?.file_path || '')) score += 8;
  if ((media?.title || '').trim()) score += 4;
  return Math.max(55, Math.min(98, score));
};

const clampText = (value, max = 240) => {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > max ? `${clean.slice(0, Math.max(0, max - 1)).trim()}…` : clean;
};

const firstSentence = (value, max = 160) => {
  const clean = clampText(value, Math.max(max * 2, max));
  if (!clean) return '';
  const parts = clean.split(/(?<=[.!?])\s+/).filter(Boolean);
  return clampText(parts[0] || clean, max);
};

const uniqTags = (...values) => {
  const seen = new Set();
  const out = [];
  values.flat().forEach((value) => {
    const clean = String(value || '').trim();
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(clean);
  });
  return out;
};

const clampScore = (value, min = 0, max = 100) => (
  Math.max(min, Math.min(max, Math.round(Number(value) || 0)))
);

const tokenizeWords = (...values) => {
  const merged = values.reduce((acc, value) => acc.concat(Array.isArray(value) ? value : [value]), []);
  const tokenText = merged.filter(Boolean).join(' ').toLowerCase();
  return Array.from(new Set(
    tokenText
      .replace(/[^a-z0-9À-ÿ]+/gi, ' ')
      .split(/\s+/)
      .map((word) => word.trim())
      .filter((word) => word.length > 2)
  ));
};

const overlapCount = (sourceWords = [], targetWords = []) => {
  const source = new Set(sourceWords);
  let count = 0;
  targetWords.forEach((word) => {
    if (source.has(word)) count += 1;
  });
  return count;
};

const moderationDecisionMeta = (moderation) => {
  const decision = String(moderation?.decision || '').toLowerCase();
  const flags = Array.isArray(moderation?.flags) ? moderation.flags : [];
  if (!moderation) return { label: 'Waiting', tone: 'light', icon: 'bi-hourglass-split' };
  if (decision === 'block') return { label: 'Needs fix', tone: 'warn', icon: 'bi-exclamation-triangle-fill' };
  if (flags.length) return { label: 'Check flags', tone: 'blue', icon: 'bi-shield-exclamation' };
  return { label: 'Ready', tone: 'green', icon: 'bi-stars' };
};

const computeSlotReviewScore = (media, moderation = null, slotKeywords = []) => {
  if (!media) return 24;
  const mediaKeywords = tokenizeWords(
    media?.title,
    media?.description,
    media?.location,
    moderation?.summary,
    moderation?.reasons,
    moderation?.content_type,
    moderation?.contentType
  );
  const matches = overlapCount(slotKeywords, mediaKeywords);
  let score = computeCompatibility(media) * 0.48;
  if ((media?.title || '').trim()) score += 6;
  if ((media?.description || '').trim()) score += 8;
  if ((media?.location || '').trim()) score += 5;
  if (mediaKindOf(media) === 'video') score += 6;
  score += matches * 5;
  if (Number(media?.selected) === 1) score += 5;

  if (moderation) {
    const decision = String(moderation?.decision || '').toLowerCase();
    const flags = Array.isArray(moderation?.flags) ? moderation.flags.length : 0;
    const risk = Number(moderation?.risk_score ?? moderation?.riskScore ?? 0) || 0;
    if (decision === 'block') score -= 26;
    else score += 10;
    score -= Math.min(18, flags * 4);
    score -= Math.round(risk / 12);
  }

  return clampScore(score, 12, 99);
};


/* =========================================================================================
 * WEB SCREEN TEMPORARILY DISABLED — MOBILE-FIRST EXPERIENCE
 * The original web implementation is preserved below as ContributorStudioLegacy.
 * ========================================================================================= */
const WV_MOBILE_GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";
const WV_MOBILE_IOS_WAITLIST_URL = "/join-beta";
const WV_MOBILE_LANG_KEY = "weavevo_lang";

const WV_MOBILE_SCREEN_COPY = {
  admin: {
    en: {
      eyebrow: "MOBILE PROJECT MANAGEMENT",
      title: "Manage your Weave from the mobile app.",
      body: "Project setup, contributors, scenario management, moderation and delivery controls are designed for a more focused mobile workflow.",
    },
    fr: {
      eyebrow: "GESTION MOBILE DU PROJET",
      title: "Gérez votre Weave depuis l’app mobile.",
      body: "La gestion du projet, des contributeurs, du scénario, de la modération et de la livraison est optimisée pour une expérience mobile plus fluide.",
    },
  },
  studio: {
    en: {
      eyebrow: "MOBILE STUDIO",
      title: "Finish your Weave in the mobile Studio.",
      body: "Media selection, layouts, transitions, music, effects and final export are easier to shape from the mobile Studio experience.",
    },
    fr: {
      eyebrow: "STUDIO MOBILE",
      title: "Finalisez votre Weave dans le Studio mobile.",
      body: "La sélection des médias, les layouts, transitions, musiques, effets et l’export final sont conçus pour être plus fluides depuis le Studio mobile.",
    },
  },
  contributor: {
    en: {
      eyebrow: "MOBILE CONTRIBUTION",
      title: "Contribute to this Weave from your phone.",
      body: "Capture, upload, slot guidance, AI review and collaboration are designed around the device already in your hand.",
    },
    fr: {
      eyebrow: "CONTRIBUTION MOBILE",
      title: "Contribuez à cette Weave depuis votre téléphone.",
      body: "La capture, l’upload, le guidage par slot, la revue IA et la collaboration sont pensés autour du mobile.",
    },
  },
};

export default function ContributorStudio() {
  const [wvMobileLang, setWvMobileLang] = useState(() => {
    try {
      const raw = localStorage.getItem(WV_MOBILE_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
      return String(raw).toLowerCase().startsWith("fr") ? "fr" : "en";
    } catch (_) {
      return "en";
    }
  });

  useEffect(() => {
    const refreshLang = () => {
      try {
        const raw = localStorage.getItem(WV_MOBILE_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
        setWvMobileLang(String(raw).toLowerCase().startsWith("fr") ? "fr" : "en");
      } catch (_) { }
    };
    window.addEventListener("storage", refreshLang);
    window.addEventListener("focus", refreshLang);
    return () => {
      window.removeEventListener("storage", refreshLang);
      window.removeEventListener("focus", refreshLang);
    };
  }, []);

  const baseCopy = WV_MOBILE_SCREEN_COPY.contributor[wvMobileLang] || WV_MOBILE_SCREEN_COPY.contributor.en;
  const labels = wvMobileLang === "fr"
    ? {
        google: "Télécharger sur Google Play",
        apple: "App Store / iOS",
        soon: "Bientôt disponible",
      }
    : {
        google: "Get it on Google Play",
        apple: "App Store / iOS",
        soon: "Coming soon",
      };

  return (
    <div className="wv-mobile-screen-gate">
      <div className="wv-mobile-screen-orb wv-mobile-screen-orb-a" aria-hidden="true" />
      <div className="wv-mobile-screen-orb wv-mobile-screen-orb-b" aria-hidden="true" />

      <section className="wv-mobile-screen-card" aria-labelledby="wv-mobile-screen-title">
        <div className="wv-mobile-screen-visual">
          <img
            src={web2mobile}
            alt="Weavevo web and mobile experience"
            className="wv-mobile-screen-visual-img"
          />
        </div>

        <div className="wv-mobile-screen-copy">
          <span className="wv-mobile-screen-eyebrow">{baseCopy.eyebrow}</span>
          <h1 id="wv-mobile-screen-title">{baseCopy.title}</h1>
          <p className="wv-mobile-screen-lead">{baseCopy.body}</p>


          <div className="wv-mobile-screen-actions">
            <a
              className="wv-mobile-screen-store is-google"
              href={WV_MOBILE_GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={labels.google}
            >
              <i className="bi bi-google-play" />
              <span>{labels.google}</span>
            </a>

            <a
              className="wv-mobile-screen-store is-apple"
              href={WV_MOBILE_IOS_WAITLIST_URL}
              aria-label={`${labels.apple} — ${labels.soon}`}
            >
              <i className="bi bi-apple" />
              <span>
                <strong>{labels.apple}</strong>
                <small>{labels.soon}</small>
              </span>
            </a>
          </div>

        </div>
      </section>

      <style>{`
        .wv-mobile-screen-gate,
        .wv-mobile-screen-gate * { box-sizing: border-box; }
        .wv-mobile-screen-gate {
          position: relative;
          min-height: calc(100vh - 70px);
          width: 100%;
          overflow: hidden;
          display: grid;
          place-items: center;
          padding: clamp(28px, 5vw, 72px);
          background:
            radial-gradient(circle at 18% 18%, rgba(70,154,255,.14), transparent 32%),
            radial-gradient(circle at 82% 74%, rgba(139,92,246,.12), transparent 34%),
            linear-gradient(180deg, #fbfdff 0%, #f5f8ff 100%);
          color: #08111e;
        }
        .wv-mobile-screen-orb {
          position: absolute;
          width: 340px;
          height: 340px;
          border-radius: 50%;
          filter: blur(60px);
          opacity: .32;
          pointer-events: none;
        }
        .wv-mobile-screen-orb-a { background:#5ab7ff; top:-150px; left:-80px; }
        .wv-mobile-screen-orb-b { background:#8b5cf6; right:-120px; bottom:-170px; }
        .wv-mobile-screen-card {
          position: relative;
          z-index: 1;
          width: min(1040px, 100%);
          min-height: 560px;
          display: grid;
          grid-template-columns: minmax(260px,.78fr) minmax(0,1.22fr);
          align-items: center;
          gap: clamp(34px,6vw,86px);
          padding: clamp(34px,5vw,70px);
          border: 1px solid rgba(10,32,70,.09);
          border-radius: 34px;
          background: rgba(255,255,255,.91);
          box-shadow: 0 35px 100px rgba(21,53,95,.12);
          backdrop-filter: blur(18px);
        }
        .wv-mobile-screen-visual {
          width:min(360px,82vw);
          justify-self:center;
          border-radius:28px;
          overflow:hidden;
          background:#eef3f8;
          box-shadow:0 28px 64px rgba(8,19,37,.18);
          transform:rotate(-1.5deg);
        }
        .wv-mobile-screen-visual-img {
          display:block;
          width:100%;
          height:auto;
          object-fit:cover;
        }
        .wv-mobile-screen-copy { min-width:0; }
        .wv-mobile-screen-eyebrow {
          display:inline-flex;
          padding:7px 11px;
          border-radius:999px;
          background:#eef8ff;
          color:#087ea4;
          font-size:12px;
          font-weight:850;
          letter-spacing:.13em;
        }
        .wv-mobile-screen-copy h1 {
          margin:18px 0;
          max-width:720px;
          font-size:clamp(2.5rem,5vw,4.8rem);
          line-height:.98;
          letter-spacing:-.055em;
          color:#07111f;
        }
        .wv-mobile-screen-lead {
          max-width:700px;
          margin:0;
          color:#415169;
          font-size:clamp(1rem,1.45vw,1.17rem);
          line-height:1.72;
        }
        .wv-mobile-screen-actions { display:flex; flex-wrap:wrap; gap:12px; margin-top:28px; }
        .wv-mobile-screen-store {
          min-height:58px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:11px;
          padding:10px 18px;
          border-radius:16px;
          text-decoration:none!important;
          font-weight:800;
          transition:transform .18s ease, box-shadow .18s ease;
        }
        .wv-mobile-screen-store:hover { transform:translateY(-2px); }
        .wv-mobile-screen-store > i { font-size:1.5rem; }
        .wv-mobile-screen-store.is-google {
          background:#08111e;
          color:#fff;
          box-shadow:0 14px 28px rgba(8,17,30,.18);
        }
        .wv-mobile-screen-store.is-apple {
          border:1px solid #d8e1ec;
          background:#fff;
          color:#08111e;
        }
        .wv-mobile-screen-store.is-apple span { display:grid; line-height:1.15; }
        .wv-mobile-screen-store.is-apple small {
          margin-top:3px;
          color:#6f7d91;
          font-size:10px;
          font-weight:700;
        }
        @media (max-width:820px) {
          .wv-mobile-screen-gate { padding:22px 16px; }
          .wv-mobile-screen-card {
            grid-template-columns:1fr;
            padding:30px 22px;
            border-radius:26px;
            text-align:center;
          }
          .wv-mobile-screen-visual { width:min(330px,88vw); transform:none; }
          .wv-mobile-screen-copy h1,
          .wv-mobile-screen-lead { margin-left:auto; margin-right:auto; }
          .wv-mobile-screen-actions { justify-content:center; }
        }
      `}</style>
    </div>
  );
}

/* Original web implementation preserved below. */

function ContributorStudioLegacy() {
  const { id } = useParams();
  const { token } = useAuth();

  const [me, setMe] = useState({ username: '', name: '' });
  const [usersByUsername, setUsersByUsername] = useState({});
  const [project, setProject] = useState(null);
  const [roles, setRoles] = useState([]);
  const [scenarioForm, setScenarioForm] = useState({ title: '', description: '', steps: [] });
  const [projectMedia, setProjectMedia] = useState([]);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [leftPanelTab, setLeftPanelTab] = useState('contributions');
  const [libraryTypeFilter, setLibraryTypeFilter] = useState('all');
  const [libraryUseExisting, setLibraryUseExisting] = useState(true);
  const [myLibrary, setMyLibrary] = useState([]);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [libraryAttachBusyId, setLibraryAttachBusyId] = useState(null);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [selectedContributionId, setSelectedContributionId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [panelIntensity, setPanelIntensity] = useState(58);
  const [notice, setNotice] = useState({ type: '', text: '' });
  const [confirmBubble, setConfirmBubble] = useState({ open: false, media: null, text: '', x: null, y: null });
  const [slotCoachExpanded, setSlotCoachExpanded] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', location: '', description: '', file: null });
  const [aiBusy, setAiBusy] = useState('');
  const [aiReferenceImage, setAiReferenceImage] = useState('');
  const [moderationById, setModerationById] = useState({});
  const [slotReviewBusy, setSlotReviewBusy] = useState(false);

  const fileInputRef = useRef(null);
  const moderationCacheRef = useRef(new Map());
  const noticeTimerRef = useRef(null);

  useEffect(() => {
    if (!notice?.text) {
      if (noticeTimerRef.current) {
        window.clearTimeout(noticeTimerRef.current);
        noticeTimerRef.current = null;
      }
      return undefined;
    }

    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);

    noticeTimerRef.current = window.setTimeout(() => {
      setNotice({ type: '', text: '' });
      noticeTimerRef.current = null;
    }, 2800);

    return () => {
      if (noticeTimerRef.current) {
        window.clearTimeout(noticeTimerRef.current);
        noticeTimerRef.current = null;
      }
    };
  }, [notice]);

  useEffect(() => () => {
    if (noticeTimerRef.current) {
      window.clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    try {
      const sourceToken = token || localStorage.getItem('kc_token') || localStorage.getItem('token');
      if (!sourceToken) return;
      const decoded = jwtDecode(sourceToken);
      const username = decoded?.preferred_username || '';
      const name = decoded?.name || decoded?.given_name || username || 'Weaver';
      setMe({ username, name });
    } catch {
      // ignore decode issues
    }
  }, [token]);

  const authHeaders = useMemo(() => {
    const sourceToken = token || localStorage.getItem('kc_token') || localStorage.getItem('token');
    return sourceToken ? { Authorization: `Bearer ${sourceToken}` } : {};
  }, [token]);

  const libraryHeaders = useMemo(() => ({
    ...authHeaders,
    'Content-Type': 'application/json',
  }), [authHeaders]);

  const fetchModerationForMedia = async (mediaId, { force = false } = {}) => {
    const key = String(mediaId || '').trim();
    if (!key) return null;

    if (!force && moderationCacheRef.current.has(key)) {
      return moderationCacheRef.current.get(key) || null;
    }

    try {
      const response = await fetch(`/media/${key}/moderation`, { headers: authHeaders });
      if (!response.ok) throw new Error('moderation');
      const payload = await response.json().catch(() => null);
      moderationCacheRef.current.set(key, payload || null);
      return payload || null;
    } catch {
      moderationCacheRef.current.set(key, null);
      return null;
    }
  };

  const syncMyLibrary = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const mine = String(me?.username || '').toLowerCase();
    const normalized = list.filter((media) => {
      if (!mine) return false;
      const owner = normalizeUser(media);
      return owner ? owner === mine : true;
    }).map((media) => {
      const playbackSource = buildPlaybackSource(media);
      const previewSource = buildPreviewSource(media);
      const mimeType = String(
        media?.mime_type || media?.mimeType || media?.type || media?.file_type || media?.content_type || media?.kind || ''
      );
      return {
        ...media,
        file_path: playbackSource || previewSource,
        preview_path: previewSource || playbackSource,
        filename: media?.filename || media?.file_name || media?.name || '',
        mime_type: mimeType || media?.mime_type || '',
        created_by: media?.created_by || media?.username || media?.owner || me?.username || '',
        contributor: media?.contributor || media?.created_by || media?.username || media?.owner || me?.username || '',
      };
    });

    const unique = [];
    const seen = new Set();
    normalized.forEach((media) => {
      const key = mediaUniqueKey(media);
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(media);
    });

    unique.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
    setMyLibrary(unique);
    return unique;
  };

  const fetchLibraryBucket = async (type = '') => {
    const PAGE_SIZE = 100;
    const MAX_PAGES = 20;
    let offset = 0;
    let page = 0;
    let allRows = [];

    while (page < MAX_PAGES) {
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));

      const response = await fetch(`/media?${params.toString()}`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`library-${type || 'all'}`);

      const payload = await response.json().catch(() => []);
      const safeRows = extractRowsFromPayload(payload);
      allRows = allRows.concat(safeRows);

      if (safeRows.length < PAGE_SIZE) break;
      offset += safeRows.length;
      page += 1;
    }

    return allRows;
  };

  const loadMyLibrary = async () => {
    if (!Object.keys(authHeaders).length) {
      setMyLibrary([]);
      return [];
    }

    setLibraryBusy(true);
    try {
      const attempts = await Promise.allSettled([
        fetchLibraryBucket(''),
        fetchLibraryBucket('image'),
        fetchLibraryBucket('video'),
      ]);

      const merged = [];
      attempts.forEach((result) => {
        if (result.status === 'fulfilled' && Array.isArray(result.value)) {
          merged.push(...result.value);
        }
      });

      if (merged.length) return syncMyLibrary(merged);
      throw new Error('empty-library');
    } catch {
      try {
        const response = await fetch('/linkedMedia', { headers: authHeaders });
        if (!response.ok) throw new Error('library-fallback');
        const payload = await response.json().catch(() => []);
        return syncMyLibrary(extractRowsFromPayload(payload));
      } catch {
        return syncMyLibrary(projectMedia);
      }
    } finally {
      setLibraryBusy(false);
    }
  };

  const loadScenario = async () => {
    try {
      const response = await fetch(`/projects/${id}/scenarios`);
      if (!response.ok) throw new Error('scenarios');
      const data = await response.json();
      const scenario = Array.isArray(data) ? (data[0] || {}) : (data || {});
      const steps = safeParseSteps(scenario?.steps).map((step, index) => ({ id: step?.id ?? index + 1, ...step }));
      setScenarioForm({
        title: scenario?.title || '',
        description: scenario?.description || '',
        steps,
      });
      if (!currentStepId && steps.length) setCurrentStepId(steps[0].id);
      return;
    } catch {
      // fallback below
    }

    try {
      const response = await fetch(`/projects/${id}/scenario`);
      if (!response.ok) return;
      const scenario = await response.json();
      const steps = safeParseSteps(scenario?.steps).map((step, index) => ({ id: step?.id ?? index + 1, ...step }));
      setScenarioForm({
        title: scenario?.title || '',
        description: scenario?.description || '',
        steps,
      });
      if (!currentStepId && steps.length) setCurrentStepId(steps[0].id);
    } catch {
      // ignore
    }
  };

  const loadAll = async () => {
    await loadScenario();

    try {
      const [mediaRes, projectRes, rolesRes, messagesRes] = await Promise.allSettled([
        fetch(`/projects/${id}/media`),
        fetch(`/projects/${id}`),
        fetch(`/projects/${id}/roles`),
        fetch(`/projects/${id}/messages`),
      ]);

      if (mediaRes.status === 'fulfilled' && mediaRes.value.ok) {
        const rows = await mediaRes.value.json();
        const list = Array.isArray(rows) ? rows : [];
        setProjectMedia(list);
      }

      if (projectRes.status === 'fulfilled' && projectRes.value.ok) {
        const row = await projectRes.value.json();
        setProject(row || null);
      }

      if (rolesRes.status === 'fulfilled' && rolesRes.value.ok) {
        const rows = await rolesRes.value.json();
        setRoles(Array.isArray(rows) ? rows : []);
      }

      if (messagesRes.status === 'fulfilled' && messagesRes.value.ok) {
        const rows = await messagesRes.value.json();
        setMessages(Array.isArray(rows) ? rows : []);
      }
    } catch {
      setNotice({ type: 'danger', text: 'Unable to fully load project data.' });
    }
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  useEffect(() => {
    if (!Object.keys(authHeaders).length) return;
    loadMyLibrary();
  }, [authHeaders, me?.username, id]);

  useEffect(() => {
    if (!Object.keys(authHeaders).length) return;
    let alive = true;
    (async () => {
      try {
        const response = await fetch('/users', { headers: authHeaders });
        if (!response.ok) return;
        const rows = await response.json();
        if (!alive) return;
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((user) => {
          if (user?.username) map[user.username] = user;
        });
        setUsersByUsername(map);
      } catch {
        // ignore
      }
    })();
    return () => { alive = false; };
  }, [authHeaders]);


  useEffect(() => {
    if (currentStepId != null) return;
    const first = scenarioForm?.steps?.[0]?.id;
    if (first != null) setCurrentStepId(first);
  }, [scenarioForm, currentStepId]);

  const myRole = useMemo(() => {
    const mine = String(me?.username || '').toLowerCase();
    const row = (roles || []).find((role) => String(role?.username || '').toLowerCase() === mine);
    return String(row?.role || '').toLowerCase().replace('_', '-');
  }, [roles, me?.username]);

  const isProjectOwner = useMemo(() => {
    const mine = String(me?.username || '').toLowerCase();
    if (!mine) return false;

    const projectOwner = String(
      project?.created_by ||
      project?.owner ||
      project?.username ||
      project?.createdBy ||
      ''
    ).toLowerCase();

    return projectOwner === mine || myRole === 'owner' || myRole === 'creator';
  }, [project, me?.username, myRole]);

  const myContributions = useMemo(() => {
    const mine = String(me?.username || '').toLowerCase();
    const rows = (projectMedia || []).filter((media) => {
      if (!mine) return true;
      return normalizeUser(media) === mine;
    });
    rows.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
    return rows;
  }, [projectMedia, me?.username]);

  const filteredContributions = useMemo(() => {
    const query = String(searchTerm || '').trim().toLowerCase();
    if (!query) return myContributions;
    return myContributions.filter((media) => {
      const haystack = [
        media?.title,
        media?.description,
        media?.location,
        media?.comment,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [myContributions, searchTerm]);

  useEffect(() => {
    if (!Object.keys(authHeaders).length || !myContributions.length) {
      setSlotReviewBusy(false);
      return;
    }

    const missing = myContributions.filter((media) => {
      const key = String(media?.id || '').trim();
      return key && !moderationCacheRef.current.has(key);
    });

    if (!missing.length) {
      setSlotReviewBusy(false);
      return;
    }

    let alive = true;
    setSlotReviewBusy(true);

    (async () => {
      const entries = await Promise.all(missing.map(async (media) => {
        const row = await fetchModerationForMedia(media?.id);
        return [String(media?.id || ''), row || null];
      }));

      if (!alive) return;

      setModerationById((prev) => {
        const next = { ...prev };
        entries.forEach(([key, value]) => {
          if (key) next[key] = value;
        });
        return next;
      });

      setSlotReviewBusy(false);
    })();

    return () => {
      alive = false;
    };
  }, [authHeaders, myContributions]);

  const filteredLibrary = useMemo(() => {
    const query = String(searchTerm || '').trim().toLowerCase();
    return (myLibrary || []).filter((media) => {
      const haystack = [
        media?.title,
        media?.description,
        media?.location,
        media?.comment,
        media?.tags,
        media?.filename,
      ].filter(Boolean).join(' ').toLowerCase();
      const kind = mediaKindOf(media);
      if (libraryTypeFilter === 'images' && kind === 'video') return false;
      if (libraryTypeFilter === 'videos' && kind !== 'video') return false;
      if (query && !haystack.includes(query)) return false;
      return true;
    });
  }, [myLibrary, searchTerm, libraryTypeFilter]);

  const contributionsByStep = useMemo(() => {
    const map = new Map();
    (myContributions || []).forEach((media) => {
      const key = media?.step_id != null ? String(media.step_id) : 'no-step';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(media);
    });
    for (const [, items] of map.entries()) {
      items.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
    }
    return map;
  }, [myContributions]);

  const slotStats = useMemo(() => {
    return (scenarioForm?.steps || []).map((step, index) => {
      const key = String(step?.id ?? index + 1);
      const items = contributionsByStep.get(key) || [];
      return {
        id: step?.id ?? index + 1,
        label: step?.location || `Slot ${index + 1}`,
        items,
        cover: items[0] || null,
      };
    });
  }, [scenarioForm, contributionsByStep]);

  const currentSlotItems = useMemo(() => {
    if (currentStepId == null) return [];
    return contributionsByStep.get(String(currentStepId)) || [];
  }, [contributionsByStep, currentStepId]);

  useEffect(() => {
    if (selectedContributionId && myContributions.some((media) => String(media.id) === String(selectedContributionId))) return;
    const fallback = currentSlotItems[0] || myContributions[0] || null;
    setSelectedContributionId(fallback?.id || null);
  }, [selectedContributionId, currentSlotItems, myContributions]);

  const selectedContribution = useMemo(() => {
    return myContributions.find((media) => String(media.id) === String(selectedContributionId)) || currentSlotItems[0] || myContributions[0] || null;
  }, [myContributions, currentSlotItems, selectedContributionId]);

  const selectedPlaybackSrc = buildPlaybackSource(selectedContribution);
  const selectedPreviewSrc = buildPreviewSource(selectedContribution);
  const selectedMime = selectedContribution?.mime_type || selectedContribution?.mimeType || selectedContribution?.type || selectedContribution?.file_type || selectedContribution?.content_type || '';
  const selectedMediaIsVideo = isVideoFile(selectedPlaybackSrc || selectedPreviewSrc, selectedMime);
  const selectedMediaUrl = withUploadsPath(
    selectedMediaIsVideo ? (selectedPlaybackSrc || selectedPreviewSrc) : (selectedPreviewSrc || selectedPlaybackSrc)
  );
  const selectedPosterUrl = withUploadsPath(selectedPreviewSrc || selectedPlaybackSrc);
  const selectedStatus = statusMeta(selectedContribution);
  const compatibility = computeCompatibility(selectedContribution);

  const previewTitle = selectedContribution?.title || scenarioForm?.title || project?.title || 'Creator preview';
  const previewSubtitle = selectedContribution?.description || 'Video du créateur : en cours';


  const activityCards = useMemo(() => {
    const fromMessages = (messages || []).slice(-3).reverse().map((msg) => ({
      key: `m-${msg?.id || msg?.created_at || Math.random()}`,
      title: msg?.user_id || msg?.username || 'Community Builder',
      text: msg?.message || 'New activity on this project.',
      avatar: avatarFor(usersByUsername, msg?.user_id || msg?.username || 'W'),
    }));

    if (fromMessages.length) return fromMessages;

    return [
      {
        key: 'fallback-1',
        title: 'Community Builder',
        text: selectedContribution
          ? `Your clip “${selectedContribution.title || 'Untitled'}” keeps the final video moving forward.`
          : 'Upload your first contribution to feed the creator preview.',
        avatar: avatarFor(usersByUsername, me?.username || 'W'),
      },
    ];
  }, [messages, usersByUsername, selectedContribution, me?.username]);

  const handleSelectContribution = (media) => {
    if (!media) return;
    setSelectedContributionId(media.id);
    if (media?.step_id != null) setCurrentStepId(media.step_id);
  };

  const handleSelectSlot = (slotId) => {
    setCurrentStepId(slotId);
    const first = contributionsByStep.get(String(slotId))?.[0] || null;
    if (first) setSelectedContributionId(first.id);
  };

  const handleAttachLibraryMedia = async (media) => {
    const mediaId = media?.id ?? media?.media_id ?? media?.mediaId;
    if (!mediaId) {
      setNotice({ type: 'danger', text: 'This media cannot be attached right now.' });
      return;
    }

    setLibraryAttachBusyId(mediaId);
    setNotice({ type: '', text: '' });

    try {
      const response = await fetch('/linkedMedia/existing', {
        method: 'POST',
        headers: libraryHeaders,
        body: JSON.stringify({
          media_ids: [mediaId],
          project_id: id,
          step_id: currentStepId || null,
        }),
      });

      if (!response.ok) throw new Error('attach');

      const rows = await refreshMedia();
      await loadMyLibrary();
      const attached = (Array.isArray(rows) ? rows : []).find((item) => {
        const linkedMediaId = item?.media_id ?? item?.mediaId ?? item?.original_media_id ?? null;
        return String(linkedMediaId || '') === String(mediaId) || mediaUniqueKey(item) === mediaUniqueKey(media);
      });
      if (attached) handleSelectContribution(attached);
      setLeftPanelTab('contributions');
    } catch {
      setNotice({ type: 'danger', text: 'Unable to attach this library media right now.' });
    } finally {
      setLibraryAttachBusyId(null);
    }
  };

  const openUploader = () => {
    setUploadOpen(true);
    setNotice({ type: '', text: '' });
  };

  const refreshMedia = async () => {
    const response = await fetch(`/projects/${id}/media`);
    const rows = await response.json().catch(() => []);
    const list = Array.isArray(rows) ? rows : [];
    setProjectMedia(list);
    return list;
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!uploadForm.file) {
      setNotice({ type: 'danger', text: 'Please choose an image or a video first.' });
      return;
    }

    setUploadBusy(true);
    setNotice({ type: '', text: '' });

    const safeTitle = String(uploadForm.title || '').trim() || String(uploadForm.file?.name || '').replace(/\.[^.]+$/, '') || 'Untitled';
    const safeLocation = String(uploadForm.location || '').trim();
    const safeDescription = String(uploadForm.description || '').trim();

    try {
      const uploadDataForm = new FormData();
      uploadDataForm.append('media', uploadForm.file);
      uploadDataForm.append('title', safeTitle);
      uploadDataForm.append('location', safeLocation);
      if (safeDescription) uploadDataForm.append('description', safeDescription);
      uploadDataForm.append('visibility', 'PRIVATE');

      const uploadResponse = await fetch('/upload', {
        method: 'POST',
        headers: authHeaders,
        body: uploadDataForm,
      });
      const uploadData = await uploadResponse.json().catch(() => ({}));
      const createdMediaId = uploadData?.id ?? uploadData?.media?.id ?? uploadData?.media_id ?? null;
      if (!uploadResponse.ok || !createdMediaId) {
        throw new Error(uploadData?.error || 'upload failed');
      }

      const attachResponse = await fetch('/linkedMedia/existing', {
        method: 'POST',
        headers: libraryHeaders,
        body: JSON.stringify({
          media_ids: [createdMediaId],
          project_id: id,
          step_id: currentStepId || null,
        }),
      });

      if (!attachResponse.ok) {
        await loadMyLibrary();
        setLeftPanelTab('library');
        throw new Error('attach failed');
      }

      const rows = await refreshMedia();
      await loadMyLibrary();
      const attached = (Array.isArray(rows) ? rows : []).find((item) => {
        const linkedMediaId = item?.media_id ?? item?.mediaId ?? item?.original_media_id ?? null;
        return String(linkedMediaId || '') === String(createdMediaId);
      });

      setUploadOpen(false);
      setUploadForm({ title: '', location: '', description: '', file: null });
      setLeftPanelTab('contributions');
      if (attached?.id != null) setSelectedContributionId(attached.id);
      setNotice({ type: 'success', text: 'Contribution uploaded, saved in your library, and added to the project.' });
    } catch (error) {
      if (String(error?.message || '').includes('attach failed')) {
        setNotice({ type: 'warning', text: 'Media uploaded to your library, but it could not be linked to the project automatically.' });
      } else {
        setNotice({ type: 'danger', text: 'Unable to upload this contribution right now.' });
      }
    } finally {
      setUploadBusy(false);
    }
  };

  const openDeleteConfirm = (media, anchorEl = null) => {
    if (!media) return;
    const mine = String(me?.username || '').toLowerCase();
    const owner = normalizeUser(media);
    const canDelete = mine && owner === mine;
    if (!canDelete) return;

    const rect = anchorEl?.getBoundingClientRect?.();
    const bubbleWidth = 320;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const maxLeft = Math.max(14, viewportWidth - bubbleWidth - 14);
    const left = rect ? Math.min(Math.max(14, rect.left + rect.width - bubbleWidth), maxLeft) : null;
    const top = rect ? Math.max(96, rect.top - 14) : null;

    setConfirmBubble({
      open: true,
      media,
      text: `Delete “${media?.title || 'this contribution'}” from the project?`,
      x: Number.isFinite(left) ? left : null,
      y: Number.isFinite(top) ? top : null,
    });
  };

  const closeDeleteConfirm = () => {
    setConfirmBubble({ open: false, media: null, text: '', x: null, y: null });
  };

  const removeContribution = async (media) => {
    if (!media) return;
    const mine = String(me?.username || '').toLowerCase();
    const owner = normalizeUser(media);
    const canDelete = mine && owner === mine;
    if (!canDelete) return;

    closeDeleteConfirm();

    try {
      const response = await fetch(`/linkedMedia/${media.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('delete');
      await refreshMedia();
      await loadMyLibrary();
      setNotice({ type: 'success', text: 'Contribution deleted.' });
    } catch {
      setNotice({ type: 'danger', text: 'Unable to delete this contribution.' });
    }
  };

  const selectedSlotMeta = slotStats.find((slot) => String(slot.id) === String(currentStepId)) || null;
  const selectedStepDefinition = useMemo(() => (
    (scenarioForm?.steps || []).find((step) => String(step?.id) === String(currentStepId)) || null
  ), [scenarioForm, currentStepId]);
  const selectedModerationKey = String(selectedContribution?.id || '');
  const hasSelectedReview = !!(selectedModerationKey && Object.prototype.hasOwnProperty.call(moderationById, selectedModerationKey));
  const selectedModeration = selectedModerationKey ? (moderationById[selectedModerationKey] ?? null) : null;
  const selectedDecisionMeta = moderationDecisionMeta(selectedModeration);

  const slotKeywords = useMemo(() => tokenizeWords(
    selectedSlotMeta?.label,
    selectedStepDefinition?.location,
    selectedStepDefinition?.description,
    selectedStepDefinition?.technical_tip,
    selectedStepDefinition?.reference,
    scenarioForm?.title,
    project?.title
  ), [selectedSlotMeta, selectedStepDefinition, scenarioForm, project]);

  const currentSlotRankings = useMemo(() => {
    return (currentSlotItems || []).map((media) => {
      const moderation = moderationById[String(media?.id || '')] ?? null;
      const score = computeSlotReviewScore(media, moderation, slotKeywords);
      const meta = moderationDecisionMeta(moderation);
      const reasons = [];

      if ((media?.description || '').trim()) reasons.push('Has context');
      if ((media?.location || '').trim()) reasons.push('Tagged');
      if (mediaKindOf(media) === 'video') reasons.push('Motion ready');
      if (moderation?.summary) reasons.push(firstSentence(moderation.summary, 60));
      if (!reasons.length) reasons.push('Fresh upload');

      return {
        id: media?.id,
        media,
        score,
        meta,
        moderation,
        reasons: reasons.slice(0, 2),
      };
    }).sort((a, b) => b.score - a.score);
  }, [currentSlotItems, moderationById, slotKeywords]);

  const selectedRanking = useMemo(() => {
    return currentSlotRankings.find((item) => String(item?.id) === String(selectedContribution?.id)) || null;
  }, [currentSlotRankings, selectedContribution]);

  const slotAcceptanceCards = useMemo(() => {
    const rows = slotStats.length ? slotStats : [{ id: 'no-step', label: 'General slot', items: myContributions }];
    return rows.map((slot) => {
      const stepDef = (scenarioForm?.steps || []).find((step) => String(step?.id) === String(slot.id)) || null;
      const localKeywords = tokenizeWords(
        slot?.label,
        stepDef?.location,
        stepDef?.description,
        stepDef?.technical_tip,
        stepDef?.reference,
        scenarioForm?.title,
        project?.title
      );

      const items = Array.isArray(slot?.items) ? slot.items : [];
      const reviewedItems = items.filter((media) => Object.prototype.hasOwnProperty.call(moderationById, String(media?.id || '')));
      const accepted = reviewedItems.filter((media) => String(moderationById[String(media?.id || '')]?.decision || '').toLowerCase() !== 'block').length;
      const blocked = reviewedItems.filter((media) => String(moderationById[String(media?.id || '')]?.decision || '').toLowerCase() === 'block').length;
      const pending = Math.max(0, items.length - reviewedItems.length);
      const averageScore = items.length
        ? Math.round(items.reduce((sum, media) => sum + computeSlotReviewScore(media, moderationById[String(media?.id || '')] ?? null, localKeywords), 0) / items.length)
        : 0;
      const acceptanceRate = items.length ? Math.round((accepted / items.length) * 100) : 0;
      const reviewedAcceptanceRate = reviewedItems.length ? Math.round((accepted / reviewedItems.length) * 100) : 0;

      return {
        id: slot.id,
        label: slot?.label || 'General slot',
        total: items.length,
        accepted,
        blocked,
        pending,
        acceptanceRate,
        reviewedAcceptanceRate,
        averageScore,
      };
    });
  }, [slotStats, myContributions, moderationById, scenarioForm, project]);

  const slotReviewMap = useMemo(() => {
    const map = {};
    slotAcceptanceCards.forEach((item) => {
      map[String(item.id)] = item;
    });
    return map;
  }, [slotAcceptanceCards]);

  const selectedSlotAcceptance = selectedSlotMeta
    ? (slotReviewMap[String(selectedSlotMeta.id)] || null)
    : (slotAcceptanceCards[0] || null);

  const selectedAcceptanceRate = selectedRanking?.score ?? computeSlotReviewScore(selectedContribution, selectedModeration, slotKeywords);
  const leaderScore = currentSlotRankings[0]?.score || 0;
  const scoreGap = leaderScore && selectedRanking ? Math.max(0, leaderScore - selectedRanking.score) : 0;

  const contributorHints = useMemo(() => {
    if (!selectedContribution) {
      return ['Select a contribution to unlock AI review.', 'Switch slot to compare your best take.', 'Use Add new to upload a stronger alternate.'];
    }

    const hints = [];
    const flagList = Array.isArray(selectedModeration?.flags) ? selectedModeration.flags : [];
    const slotFocus = uniqTags(...slotKeywords).slice(0, 3);

    if (!(selectedContribution?.title || '').trim()) hints.push('Add a clear title. The Weaver scans titles first before opening the clip.');
    if (!(selectedContribution?.description || '').trim()) hints.push('Add one sentence explaining what happens in this take. It improves confidence fast.');
    if (!(selectedContribution?.location || '').trim()) hints.push('Add location or tags so the clip is easier to sort inside the slot.');

    if (String(selectedModeration?.decision || '').toLowerCase() === 'block') {
      hints.push(`AI flagged this media${flagList.length ? ` (${flagList.slice(0, 2).join(', ')})` : ''}. Upload a cleaner alternate or trim the risky part.`);
    } else if (flagList.length) {
      hints.push(`AI spotted ${flagList.slice(0, 2).join(', ')}. A safer crop or shorter extract could move this to ready.`);
    } else if (hasSelectedReview) {
      hints.push('AI review is clean. Focus now on making the intent clearer for the Weaver.');
    } else {
      hints.push('This media still needs AI review. Refresh once to load its moderation verdict.');
    }

    if (selectedMediaIsVideo) hints.push('For video, keep the strongest 5–8 seconds. Shorter and steadier clips are picked faster.');
    else hints.push('For image, crop tighter around the subject so it reads well in the slot strip.');

    if (scoreGap >= 10) {
      hints.push(`You are ${scoreGap} points behind the current best pick. A clearer title and tighter framing can close that gap quickly.`);
    }

    if (selectedSlotAcceptance?.acceptanceRate != null) {
      if (selectedSlotAcceptance.acceptanceRate < 40) hints.push(`This slot is hard right now (${selectedSlotAcceptance.acceptanceRate}% accepted). Match the brief very literally.`);
      else if (selectedSlotAcceptance.acceptanceRate >= 70) hints.push(`This slot is welcoming (${selectedSlotAcceptance.acceptanceRate}% accepted). Small polish can make you stand out.`);
    }

    if (slotFocus.length) {
      hints.push(`For this slot, lean into: ${slotFocus.join(', ')}.`);
    }

    return uniqTags(hints).slice(0, 5);
  }, [selectedContribution, selectedModeration, hasSelectedReview, selectedMediaIsVideo, scoreGap, selectedSlotAcceptance, slotKeywords]);

  const contributorNudges = useMemo(() => {
    const notes = [];
    if ((selectedModeration?.summary || '').trim()) notes.push(firstSentence(selectedModeration.summary, 96));
    if (selectedRanking) notes.push(`Weaver pick chance for this slot: ${selectedRanking.score}%.`);
    if (selectedSlotAcceptance) notes.push(`${selectedSlotAcceptance.accepted}/${selectedSlotAcceptance.total} accepted in this slot.`);
    if (!notes.length) notes.push('Upload or select a contribution to see live AI coaching.');
    return notes.slice(0, 3);
  }, [selectedModeration, selectedRanking, selectedSlotAcceptance]);

  useEffect(() => {
    if (!selectedContribution?.id || !Object.keys(authHeaders).length) return;
    const key = String(selectedContribution.id);
    if (Object.prototype.hasOwnProperty.call(moderationById, key)) return;

    let alive = true;
    (async () => {
      const row = await fetchModerationForMedia(selectedContribution.id);
      if (!alive) return;
      setModerationById((prev) => ({ ...prev, [key]: row || null }));
    })();

    return () => {
      alive = false;
    };
  }, [selectedContribution?.id, authHeaders, moderationById]);

  const handleAiRefreshAnalysis = async () => {
    if (!selectedContribution?.id) {
      setNotice({ type: 'warning', text: 'Select a contribution first.' });
      return;
    }

    setAiBusy('review');
    const row = await fetchModerationForMedia(selectedContribution.id, { force: true });
    setModerationById((prev) => ({ ...prev, [String(selectedContribution.id)]: row || null }));
    setAiBusy('');
    setNotice({
      type: row ? 'success' : 'warning',
      text: row ? 'AI review refreshed for the selected media.' : 'No saved AI review was found for this media yet.'
    });
  };

  const handleAiGenerateReference = async () => {
    if (!selectedContribution && !selectedStepDefinition && !selectedSlotMeta) {
      setNotice({ type: 'warning', text: 'Pick a slot or contribution first.' });
      return;
    }

    setAiBusy('reference');
    try {
      const prompt = [
        selectedStepDefinition?.location || selectedSlotMeta?.label || 'Cinematic scene',
        selectedStepDefinition?.description || selectedContribution?.description || 'Clean cinematic composition',
        selectedContribution?.title ? `Reference the mood of ${selectedContribution.title}` : '',
        selectedMediaIsVideo ? 'frame from a cinematic travel video' : 'high quality still frame',
      ].filter(Boolean).join('. ');

      const response = await fetch('/ai/generate-slot-image', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, aspect_ratio: '16:9' }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.image_url) throw new Error(payload?.error || 'reference');

      setAiReferenceImage(withUploadsPath(payload.image_url));
      setNotice({ type: 'success', text: 'AI generated a reference frame for this slot.' });
    } catch {
      setNotice({ type: 'danger', text: 'Unable to generate the AI reference frame right now.' });
    } finally {
      setAiBusy('');
    }
  };

  const handleAiSmartPick = () => {
    if (!currentSlotRankings.length) {
      setNotice({ type: 'warning', text: 'No contribution available in this slot yet.' });
      return;
    }

    const top = currentSlotRankings[0];
    if (top?.media) {
      handleSelectContribution(top.media);
      setNotice({
        type: 'success',
        text: `AI highlighted “${top.media?.title || 'Untitled contribution'}” as the easiest pick for ${selectedSlotMeta?.label || 'this slot'}.`
      });
    }
  };

  const usedInFinalCount = myContributions.filter((media) => Number(media?.selected) === 1).length;
  const noticeTone = ['success', 'warning', 'danger'].includes(String(notice?.type || '').toLowerCase())
    ? String(notice?.type || '').toLowerCase()
    : 'info';
  const confirmBubbleStyle = confirmBubble?.open && confirmBubble?.x != null && confirmBubble?.y != null
    ? { left: `${confirmBubble.x}px`, top: `${confirmBubble.y}px` }
    : null;
  const selectedRankPosition = selectedRanking
    ? (currentSlotRankings.findIndex((item) => String(item?.id) === String(selectedContribution?.id)) + 1)
    : 0;

  return (
    <div className="wv-studio-v2 csv2-wrap">
      {notice?.text ? (
        <div className={`csv2-floating-toast csv2-floating-toast-${noticeTone}`} role="status" aria-live="polite">
          <div className="csv2-floating-toast-dot" />
          <div className="csv2-floating-toast-copy">{notice.text}</div>
          <button
            type="button"
            className="csv2-floating-toast-close"
            onClick={() => setNotice({ type: '', text: '' })}
            aria-label="Dismiss message"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      ) : null}

      {confirmBubble?.open ? (
        <div
          className={`csv2-confirm-bubble ${confirmBubbleStyle ? '' : 'is-fallback'}`}
          style={confirmBubbleStyle || undefined}
          role="alertdialog"
          aria-modal="false"
        >
          <div className="csv2-confirm-bubble-kicker">Confirm delete</div>
          <div className="csv2-confirm-bubble-text">{confirmBubble.text}</div>
          <div className="csv2-confirm-bubble-actions">
            <button type="button" className="csv2-soft-btn csv2-compact-btn" onClick={closeDeleteConfirm}>
              Cancel
            </button>
            <button type="button" className="csv2-primary-btn csv2-compact-btn" onClick={() => removeContribution(confirmBubble.media)}>
              Delete
            </button>
          </div>
        </div>
      ) : null}

      <div className="csv2-shell">
        <div className="csv2-main-grid">
          <aside className="csv2-card csv2-left-card">
            <div className="csv2-left-tabs">
              <button
                type="button"
                className={leftPanelTab === 'contributions' ? 'active' : ''}
                onClick={() => setLeftPanelTab('contributions')}
              >
                Your Contributions
              </button>
              <button
                type="button"
                className={leftPanelTab === 'library' ? 'active' : ''}
                onClick={() => setLeftPanelTab('library')}
              >
                My Library
              </button>
            </div>

            {leftPanelTab === 'library' ? (
              <div className="csv2-library-head">
                <div className="csv2-library-title-row">
                  <i className="bi bi-collection-play" />
                  <span>Personal media pool</span>
                </div>

                <div className="csv2-library-head-actions">
                  <label className="csv2-library-switch">
                    <input
                      type="checkbox"
                      checked={libraryUseExisting}
                      onChange={(e) => setLibraryUseExisting(e.target.checked)}
                    />
                    <span className="csv2-library-switch-track" />
                    <span className="csv2-library-switch-label">Use existing media</span>
                  </label>

                  <button type="button" className="csv2-soft-btn csv2-inline-btn" onClick={openUploader}>
                    <i className="bi bi-plus-lg" />
                    <span>Add new</span>
                  </button>
                </div>
              </div>
            ) : null}

            <div className="csv2-left-search">
              <i className="bi bi-search" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={leftPanelTab === 'library' ? 'Filtrer par titre...' : 'Search your contributions...'}
              />
            </div>

            {leftPanelTab === 'library' ? (
              <>
                <div className="csv2-library-toolbar">
                  <div className="csv2-library-meta">
                    {libraryBusy ? 'Loading your media…' : `${filteredLibrary.length} media in your library`}
                  </div>

                  <div className="csv2-library-toolbar-right">
                    <button
                      type="button"
                      className="csv2-library-reset"
                      onClick={() => setLibraryTypeFilter('all')}
                      title="Reset the format filter"
                    >
                      <i className="bi bi-funnel" />
                    </button>

                    <select
                      className="csv2-library-select"
                      value={libraryTypeFilter}
                      onChange={(e) => setLibraryTypeFilter(e.target.value)}
                    >
                      <option value="all">All formats</option>
                      <option value="images">Images</option>
                      <option value="videos">Videos</option>
                    </select>
                  </div>
                </div>

                <div className={`csv2-library-grid ${!libraryUseExisting ? 'is-disabled' : ''}`}>
                  {!libraryUseExisting ? (
                    <div className="csv2-empty">Turn on “Use existing media” to browse your personal library.</div>
                  ) : filteredLibrary.length ? filteredLibrary.map((media) => {
                    const playbackSrc = buildPlaybackSource(media);
                    const previewSrc = buildPreviewSource(media);
                    const fallbackSrc = buildMediaSource(media);
                    const mime = media?.mime_type || media?.mimeType || media?.type || media?.file_type || media?.content_type || '';
                    const video = mediaKindOf(media) === 'video';
                    const previewLooksImage = isImageFile(previewSrc, media?.thumb_mime_type || media?.thumbMimeType || media?.preview_mime_type || '');
                    const mediaUrl = withUploadsPath(video ? (playbackSrc || fallbackSrc || previewSrc) : (previewSrc || fallbackSrc));
                    const previewUrl = withUploadsPath(previewSrc || playbackSrc || fallbackSrc);
                    const hasRenderableMedia = Boolean(mediaUrl || previewUrl);
                    const attachId = media?.id ?? media?.media_id ?? media?.mediaId ?? mediaUniqueKey(media);
                    const busy = String(libraryAttachBusyId || '') === String(attachId);

                    return (
                      <div className="csv2-library-item" key={`library-${attachId}`}>
                        <div className="csv2-library-thumb">
                          {video ? (
                            previewLooksImage && previewUrl ? (
                              <>
                                <img src={previewUrl} alt={media?.title || 'Video preview'} loading="lazy" />
                                <span className="csv2-library-video-badge">
                                  <i className="bi bi-play-fill" />
                                  <span>Video</span>
                                </span>
                              </>
                            ) : mediaUrl ? (
                              <video
                                src={mediaUrl}
                                muted
                                playsInline
                                preload="metadata"
                                poster={previewUrl && previewUrl !== mediaUrl ? previewUrl : undefined}
                              />
                            ) : (
                              <div className="csv2-library-thumb-fallback">
                                <i className="bi bi-camera-video" />
                                <span>Video preview unavailable</span>
                              </div>
                            )
                          ) : previewUrl ? (
                            <img src={previewUrl} alt={media?.title || 'My library media'} loading="lazy" />
                          ) : (
                            <div className="csv2-library-thumb-fallback">
                              <i className="bi bi-image" />
                              <span>{hasRenderableMedia ? 'Preview loading…' : 'Preview unavailable'}</span>
                            </div>
                          )}
                        </div>

                        <div className="csv2-library-footer">
                          <div className="csv2-library-copy">
                            <div className="csv2-library-title">{media?.title || media?.filename || 'Untitled media'}</div>
                            <div className="csv2-library-sub">{video ? 'Video' : 'Image'}</div>
                          </div>

                          <button
                            type="button"
                            className="csv2-library-add"
                            onClick={() => handleAttachLibraryMedia(media)}
                            disabled={busy}
                            title="Add this media to the current slot"
                          >
                            {busy ? <i className="bi bi-arrow-repeat" /> : <i className="bi bi-plus-lg" />}
                          </button>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="csv2-empty">No media found in your library.</div>
                  )}
                </div>
              </>
            ) : (
              <div className="csv2-left-list">
                {filteredContributions.length ? filteredContributions.map((media) => {
                  const playbackSrc = buildPlaybackSource(media);
                  const previewSrc = buildPreviewSource(media);
                  const mime = media?.mime_type || media?.mimeType || media?.type || media?.file_type || media?.content_type || '';
                  const video = isVideoFile(playbackSrc || buildMediaSource(media), mime);
                  const url = withUploadsPath(video ? (playbackSrc || buildMediaSource(media) || previewSrc) : (previewSrc || buildMediaSource(media)));
                  const thumbUrl = withUploadsPath(previewSrc || playbackSrc || buildMediaSource(media));
                  const showVideoThumb = video && isImageFile(previewSrc, media?.thumb_mime_type || media?.thumbMimeType || media?.preview_mime_type || '');
                  const status = statusMeta(media);
                  const isActive = String(media?.id) === String(selectedContribution?.id);
                  const contributor = media?.contributor || media?.created_by || me?.username || 'Weaver';
                  const canDelete = normalizeUser(media) === String(me?.username || '').toLowerCase();

                  return (
                    <button
                      type="button"
                      key={media.id}
                      className={`csv2-contribution-row ${isActive ? 'is-active' : ''}`}
                      onClick={() => handleSelectContribution(media)}
                    >
                      <div className="csv2-contribution-thumb">
                        {video ? (
                          showVideoThumb ? (
                            <>
                              <img src={thumbUrl} alt={media?.title || 'Contribution preview'} loading="lazy" />
                              <span className="csv2-thumb-video-badge"><i className="bi bi-play-fill" /></span>
                            </>
                          ) : (
                            <video
                              src={url}
                              muted
                              playsInline
                              preload="metadata"
                              poster={thumbUrl && thumbUrl !== url ? thumbUrl : undefined}
                            />
                          )
                        ) : (
                          <img src={thumbUrl} alt={media?.title || 'Contribution'} loading="lazy" />
                        )}
                      </div>

                      <div className="csv2-contribution-copy">
                        <div className="csv2-contribution-title">{media?.title || 'Untitled contribution'}</div>
                        <div className="csv2-contribution-meta">
                          <span>{media?.location || 'Unknown place'}</span>
                          <span>•</span>
                          <span>{formatWhen(media?.created_at)}</span>
                        </div>
                        <div className={`csv2-status csv2-status-${status.tone}`}>
                          <i className={`bi ${status.icon}`} />
                          <span>{status.label}</span>
                        </div>
                      </div>

                      <div className="csv2-contribution-side">
                        <img src={avatarFor(usersByUsername, contributor)} alt={contributor} />
                        {canDelete ? (
                          <span
                            className="csv2-delete"
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              event.stopPropagation();
                              openDeleteConfirm(media, event.currentTarget);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                event.stopPropagation();
                                openDeleteConfirm(media, event.currentTarget);
                              }
                            }}
                            title="Delete contribution"
                          >
                            <i className="bi bi-trash3" />
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                }) : (
                  <div className="csv2-empty">No contribution found for this search.</div>
                )}
              </div>
            )}
          </aside>

          <section className="csv2-card csv2-center-card">
            <div className="csv2-center-head">
              <div>
                <h2>{scenarioForm?.title || project?.title || 'Epic Ski Film'}</h2>
                <div className="csv2-subline">
                  {project?.created_by || project?.owner || 'Project creator'}
                  {myRole ? <span className="csv2-role-badge">{myRole}</span> : null}
                </div>
              </div>

              {isProjectOwner ? (
                <a
                  href={`/project/${id}/studio`}
                  className="csv2-owner-studio-link"
                  title="Open creator studio"
                >
                  <i className="bi bi-box-arrow-up-right" />
                  <span>Open Studio</span>
                </a>
              ) : null}
            </div>

            <div className="csv2-preview-stage">
              <div className="csv2-stage-media">
                {selectedContribution && selectedMediaUrl ? (
                  selectedMediaIsVideo ? (
                    <video
                      src={selectedMediaUrl}
                      controls
                      playsInline
                      preload="metadata"
                      poster={selectedPosterUrl && selectedPosterUrl !== selectedMediaUrl ? selectedPosterUrl : undefined}
                    />
                  ) : (
                    <img src={selectedMediaUrl} alt={previewTitle} />
                  )
                ) : (
                  <div className="csv2-stage-empty">
                    <i className="bi bi-camera-reels" />
                    <span>Video du créateur : en cours</span>
                  </div>
                )}
              </div>

             
            </div>

            <div className="csv2-editor-zone csv2-editor-zone-rich">
              <div className="csv2-editor-left">
                <div className="csv2-editor-kicker">{selectedSlotMeta ? `Slot #${selectedSlotMeta.id}` : 'Current contribution'}</div>
                <div className="csv2-editor-title">{previewTitle}</div>
                <div className="csv2-editor-subtitle">{previewSubtitle}</div>

                <div className="csv2-editor-inline csv2-editor-inline-rich">
                  <div className="csv2-editor-thumb csv2-editor-thumb-rich">
                    {selectedContribution && selectedMediaUrl ? (
                      selectedMediaIsVideo ? (
                        <video
                          src={selectedMediaUrl}
                          muted
                          playsInline
                          preload="metadata"
                          poster={selectedPosterUrl && selectedPosterUrl !== selectedMediaUrl ? selectedPosterUrl : undefined}
                        />
                      ) : (
                        <img src={selectedMediaUrl} alt={previewTitle} />
                      )
                    ) : (
                      <div className="csv2-editor-thumb-empty">
                        <i className="bi bi-image" />
                      </div>
                    )}
                  </div>

                  <div className="csv2-editor-stack">
                    <div className="csv2-inline-metrics">
                      <div className="csv2-inline-metric csv2-inline-metric-hero">
                        <span className="csv2-inline-label">Weaver pick chance</span>
                        <strong>{selectedAcceptanceRate}%</strong>
                        <div className="csv2-progress-ghost csv2-progress-tight">
                          <span style={{ width: `${Math.max(8, selectedAcceptanceRate)}%` }} />
                        </div>
                      </div>
                      <div className="csv2-inline-metric">
                        <span className="csv2-inline-label">AI review</span>
                        <div className="csv2-inline-status-row">
                          <span className={`csv2-pill ${selectedDecisionMeta.tone}`}>
                            <i className={`bi ${selectedDecisionMeta.icon}`} />
                            <span>{selectedDecisionMeta.label}</span>
                          </span>
                        </div>
                      </div>
                      <div className="csv2-inline-metric">
                        <span className="csv2-inline-label">Slot acceptance</span>
                        <strong>{selectedSlotAcceptance?.acceptanceRate ?? 0}%</strong>
                        <small>{selectedSlotAcceptance?.accepted ?? 0}/{selectedSlotAcceptance?.total ?? 0} accepted</small>
                      </div>
                    </div>

                    <div className="csv2-ai-summary-card">
                      <div className="csv2-suggestions-title">What AI sees on this media</div>
                      <div className="csv2-ai-summary-copy">
                        {hasSelectedReview
                          ? (firstSentence(selectedModeration?.summary, 180) || 'AI review is available for this media.')
                          : 'AI review has not been loaded yet for this media.'}
                      </div>

                      <div className="csv2-ai-summary-tags">
                        <span className={`csv2-pill ${selectedDecisionMeta.tone}`}>{selectedDecisionMeta.label}</span>
                        {selectedMediaIsVideo ? <span className="csv2-pill blue">Video</span> : <span className="csv2-pill blue">Image</span>}
                        {selectedRanking ? <span className="csv2-pill light">{selectedRanking.score}/100 fit</span> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="csv2-suggestions csv2-ai-review-panel csv2-slot-coach-panel">
                <div className="csv2-suggestions-head">
                  <div>
                    <div className="csv2-suggestions-title">AI Slot Coach</div>
                    <div className="csv2-ai-panel-sub">Selected-media context, live slot acceptance, and quick hints for the current slot.</div>
                  </div>
                  <button
                    type="button"
                    className="csv2-soft-btn csv2-compact-btn"
                    onClick={handleAiRefreshAnalysis}
                    disabled={!selectedContribution || aiBusy === 'review'}
                  >
                    {aiBusy === 'review' ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>

                <div className="csv2-slot-coach-summary">
                  <div className="csv2-slot-coach-context">
                    <span className="csv2-slot-coach-kicker">Selected media</span>
                    <strong>{selectedContribution?.title || 'No media selected yet'}</strong>
                    <span>
                      {hasSelectedReview
                        ? (firstSentence(selectedModeration?.summary, 140) || 'AI review is ready for this media.')
                        : 'AI review has not been loaded yet for this media.'}
                    </span>
                  </div>

                  <div className="csv2-ai-review-hero csv2-slot-coach-score">
                    <div className="csv2-ai-ring">
                      <svg viewBox="0 0 44 44" aria-hidden="true">
                        <circle cx="22" cy="22" r="18" pathLength="100" className="csv2-ai-ring-track" />
                        <circle
                          cx="22"
                          cy="22"
                          r="18"
                          pathLength="100"
                          className="csv2-ai-ring-value"
                          style={{ strokeDasharray: `${Math.max(6, selectedAcceptanceRate)} 100` }}
                        />
                      </svg>
                      <strong>{selectedAcceptanceRate}%</strong>
                    </div>
                    <div className="csv2-ai-hero-copy">
                      <div className="csv2-inline-label">Acceptance rate for this slot</div>
                      <div className="csv2-ai-hero-title">{selectedDecisionMeta.label}</div>
                      <div className="csv2-ai-hero-sub">
                        {selectedRankPosition ? `Ranked #${selectedRankPosition} for this slot.` : 'Waiting for a slot comparison.'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="csv2-ai-review-notes csv2-slot-coach-notes">
                  {contributorNudges.map((item, index) => (
                    <div className="csv2-suggestion-row" key={`nudge-${index}`}>
                      <i className="bi bi-stars" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="csv2-slot-coach-compact">
                  <div className="csv2-slot-coach-compact-title">Compact summary</div>
                  <div className="csv2-slot-coach-compact-grid">
                    <div className="csv2-slot-coach-chip">
                      <span>Slot acceptance</span>
                      <strong>{selectedSlotAcceptance?.acceptanceRate ?? 0}%</strong>
                    </div>
                    <div className="csv2-slot-coach-chip">
                      <span>Accepted now</span>
                      <strong>{selectedSlotAcceptance?.accepted ?? 0}/{selectedSlotAcceptance?.total ?? 0}</strong>
                    </div>
                    <div className="csv2-slot-coach-chip">
                      <span>Selected-media fit</span>
                      <strong>{selectedRanking?.score ?? selectedAcceptanceRate}/100</strong>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="csv2-soft-btn csv2-slot-coach-toggle"
                  onClick={() => setSlotCoachExpanded((value) => !value)}
                >
                  <i className={`bi ${slotCoachExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                  <span>{slotCoachExpanded ? 'Hide detailed slot hints' : 'Show detailed slot hints'}</span>
                </button>

                {slotCoachExpanded ? (
                  <div className="csv2-slot-coach-hints">
                    {contributorHints.map((item, index) => (
                      <div className="csv2-suggestion-row" key={`${item}-${index}`}>
                        <i className="bi bi-magic" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="csv2-ai-actions-row">
                  <button type="button" className="csv2-primary-btn" onClick={handleAiSmartPick} disabled={!currentSlotRankings.length}>
                    <i className="bi bi-stars" />
                    <span>Smart pick best take</span>
                  </button>
                  <button
                    type="button"
                    className="csv2-soft-btn"
                    onClick={handleAiGenerateReference}
                    disabled={aiBusy === 'reference'}
                  >
                    {aiBusy === 'reference' ? 'Generating…' : 'Generate ideal frame'}
                  </button>
                </div>

                {aiReferenceImage ? (
                  <div className="csv2-ai-reference-card">
                    <div className="csv2-inline-label">AI reference frame for this slot</div>
                    <img src={aiReferenceImage} alt="AI slot reference" />
                  </div>
                ) : null}
              </div>
            </div>

          </section>

          <aside className="csv2-card csv2-right-card csv2-right-card-rich">
            <div className="csv2-info-block csv2-info-block-featured">
              <div className="csv2-block-title">Slot Acceptance Pulse</div>
              <div className="csv2-right-sub">Acceptance is powered by the real AI moderation verdicts already stored for your media.</div>

              <div className="csv2-slot-pulse-hero">
                <div className="csv2-slot-pulse-core">
                  <span className="csv2-slot-pulse-dot" />
                  <strong>{selectedSlotAcceptance?.acceptanceRate ?? 0}%</strong>
                </div>
                <div className="csv2-slot-pulse-copy">
                  <span className="csv2-inline-label">Current slot pulse</span>
                  <strong>{selectedSlotMeta?.label || 'General slot'}</strong>
                  <span>{selectedSlotAcceptance?.accepted ?? 0} accepted • {selectedSlotAcceptance?.pending ?? 0} pending</span>
                </div>
              </div>

              <div className="csv2-metric-box csv2-metric-box-hero">
                <div className="csv2-metric-head">
                  <span>Current slot</span>
                  <span>{selectedSlotAcceptance?.acceptanceRate ?? 0}%</span>
                </div>
                <div className="csv2-progress-ghost">
                  <span style={{ width: `${Math.max(8, selectedSlotAcceptance?.acceptanceRate ?? 0)}%` }} />
                </div>
                <div className="csv2-coin-sub">
                  {selectedSlotAcceptance?.accepted ?? 0} accepted • {selectedSlotAcceptance?.blocked ?? 0} blocked • {selectedSlotAcceptance?.pending ?? 0} pending
                </div>
              </div>

              <div className="csv2-slot-acceptance-list">
                {slotAcceptanceCards.map((slot) => {
                  const active = String(slot.id) === String(currentStepId);
                  return (
                    <button type="button" key={`accept-${slot.id}`} className={`csv2-slot-accept-row ${active ? 'is-active' : ''}`} onClick={() => handleSelectSlot(slot.id)}>
                      <div className="csv2-slot-accept-copy">
                        <strong>{slot.label}</strong>
                        <span>{slot.accepted}/{slot.total} accepted • {slot.averageScore}/100 avg fit</span>
                      </div>
                      <div className="csv2-slot-accept-rate">{slot.acceptanceRate}%</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="csv2-info-block">
              <div className="csv2-block-title">Weaver choice helper</div>
              <div className="csv2-right-sub">This ranking makes it easier for the Weaver to pick the best contribution fast.</div>

              <div className="csv2-rank-list">
                {currentSlotRankings.length ? currentSlotRankings.slice(0, 4).map((item, index) => {
                  const active = String(item?.id) === String(selectedContribution?.id);
                  return (
                    <button type="button" key={`rank-${item.id}`} className={`csv2-rank-item ${active ? 'is-active' : ''}`} onClick={() => handleSelectContribution(item.media)}>
                      <div className="csv2-rank-score">#{index + 1}</div>
                      <div className="csv2-rank-copy">
                        <strong>{item?.media?.title || 'Untitled contribution'}</strong>
                        <span>{item?.score || 0}/100 • {item?.meta?.label || 'Waiting'}</span>
                        <small>{item?.reasons?.join(' • ') || 'Ready for review'}</small>
                      </div>
                    </button>
                  );
                }) : (
                  <div className="csv2-empty">Add media in this slot to unlock ranking.</div>
                )}
              </div>
            </div>

            <div className="csv2-info-block">
              <div className="csv2-block-title">Weaver&apos;s Hub</div>
              <div className="csv2-metric-box">
                <div className="csv2-metric-head">
                  <span>Creative intensity</span>
                  <span>{panelIntensity}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={panelIntensity}
                  onChange={(e) => setPanelIntensity(Number(e.target.value))}
                />
                <div className="csv2-coin-sub">Use it as your own creative target while preparing better takes.</div>
              </div>

              <div className="csv2-badges">
                <span className="csv2-pill blue">{selectedMediaIsVideo ? 'Video' : 'Image'}</span>
                <span className="csv2-pill green">{usedInFinalCount} used in final</span>
                <span className={`csv2-pill ${selectedDecisionMeta.tone}`}>{selectedDecisionMeta.label}</span>
                {slotReviewBusy ? <span className="csv2-pill light">AI scanning…</span> : null}
              </div>

              {activityCards.map((item) => (
                <div className="csv2-message-card" key={item.key}>
                  <img src={item.avatar} alt={item.title} />
                  <div>
                    <div className="csv2-message-title">{item.title}</div>
                    <div className="csv2-message-text">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>

        <section className="csv2-card csv2-slotline-card">
          <div className="csv2-slotline-head">
            <div>
              <h3>Slot Line</h3>
              <div className="csv2-slotline-sub">
                Keep the slots visible and jump directly where you want to contribute.
              </div>
            </div>

            <button type="button" className="csv2-primary-btn csv2-slotline-add" onClick={openUploader}>
              <i className="bi bi-plus-lg" />
              <span>Contribute now</span>
            </button>
          </div>

          <div className="csv2-slotline-scroll">
            {(slotStats.length ? slotStats : [{ id: 'no-step', label: 'General slot', items: myContributions, cover: myContributions[0] || null }]).map((slot, index) => {
              const active = String(slot.id) === String(currentStepId);
              const cover = slot.cover;
              const coverPlaybackSrc = buildPlaybackSource(cover);
              const coverPreviewSrc = buildPreviewSource(cover);
              const coverMime = cover?.mime_type || cover?.mimeType || cover?.type || cover?.file_type || cover?.content_type || '';
              const video = isVideoFile(coverPlaybackSrc || coverPreviewSrc, coverMime);
              const url = withUploadsPath(video ? (coverPlaybackSrc || coverPreviewSrc) : (coverPreviewSrc || coverPlaybackSrc));
              const thumbUrl = withUploadsPath(coverPreviewSrc || coverPlaybackSrc);
              const showVideoThumb = video && isImageFile(coverPreviewSrc, cover?.thumb_mime_type || cover?.thumbMimeType || cover?.preview_mime_type || '');
              const slotReview = slotReviewMap[String(slot.id)] || null;

              return (
                <button
                  type="button"
                  key={slot.id}
                  className={`csv2-slot-card ${active ? 'is-active' : ''}`}
                  onClick={() => handleSelectSlot(slot.id)}
                >
                  <div className="csv2-slot-thumb">
                    {cover && url ? (
                      video ? (
                        showVideoThumb ? (
                          <img src={thumbUrl || url} alt={slot.label} loading="lazy" />
                        ) : (
                          <video
                            src={url}
                            muted
                            playsInline
                            preload="metadata"
                            poster={thumbUrl && thumbUrl !== url ? thumbUrl : undefined}
                          />
                        )
                      ) : (
                        <img src={url} alt={slot.label} loading="lazy" />
                      )
                    ) : (
                      <div className="csv2-slot-empty">
                        <i className="bi bi-plus-lg" />
                      </div>
                    )}
                  </div>

                  <div className="csv2-slot-accept-pill">
                    {slotReview ? `${slotReview.acceptanceRate}% accepted` : 'AI pending'}
                  </div>

                  <div className="csv2-slot-copy">
                    <div className="csv2-slot-title">{slot.label || `Slot ${index + 1}`}</div>
                    <div className="csv2-slot-meta">
                      {slot.items.length} media • {slotReview ? `${slotReview.acceptanceRate}% accepted` : 'AI pending'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {uploadOpen ? (
        <div className="csv2-modal-backdrop" onClick={() => setUploadOpen(false)}>
          <div className="csv2-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="csv2-modal-head">
              <div>
                <div className="csv2-kicker">New contribution</div>
                <h3>Upload to slot {currentStepId != null ? `#${currentStepId}` : 'General'}</h3>
              </div>
              <button type="button" className="csv2-close" onClick={() => setUploadOpen(false)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={submitUpload} className="csv2-upload-form">
              <div className="csv2-form-grid">
                <label>
                  <span>Title</span>
                  <input
                    value={uploadForm.title}
                    placeholder="Optional — defaults to file name"
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </label>

                <label>
                  <span>Location</span>
                  <input
                    value={uploadForm.location}
                    placeholder="Tags / location"
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, location: e.target.value }))}
                  />
                </label>
              </div>

              <label>
                <span>Description</span>
                <textarea
                  rows="4"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </label>

              <label>
                <span>Media file</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setUploadForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                />
              </label>

              <div className="csv2-upload-actions">
                <button type="button" className="csv2-soft-btn" onClick={() => setUploadOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="csv2-primary-btn" disabled={uploadBusy}>
                  {uploadBusy ? 'Saving…' : 'Add to project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <style>{`
        :root {
          --wv-bg: #f6f7fb;
          --wv-card: rgba(255,255,255,0.92);
          --wv-border: rgba(15,23,42,0.10);
          --wv-text: #0b1220;
          --wv-muted: rgba(11,18,32,0.70);
          --wv-accent: rgba(56,104,255,1);
          --wv-accent-soft: rgba(56,104,255,0.14);
          --wv-green: rgba(16,185,129,1);
          --wv-green-soft: rgba(16,185,129,0.12);
          --wv-red: rgba(239,68,68,1);
          --wv-red-soft: rgba(239,68,68,0.12);
        }
        .wv-studio-v2.csv2-wrap {
          --wv-app-top-offset: 72px;
          min-height: 100vh;
          padding: calc(var(--wv-app-top-offset) + 12px) 14px 36px;
          background:
            radial-gradient(1200px 600px at 18% 0%, rgba(56,104,255,0.12), transparent 60%),
            radial-gradient(1000px 560px at 88% 10%, rgba(0,220,255,0.10), transparent 55%),
            var(--wv-bg);
          color: var(--wv-text);
        }
        .csv2-shell {
          margin: 0 auto;
        }
        .csv2-floating-toast {
          position: fixed;
          top: calc(var(--wv-app-top-offset) + 14px);
          right: 18px;
          z-index: 50;
          min-width: min(360px, calc(100vw - 28px));
          max-width: min(420px, calc(100vw - 28px));
          display: grid;
          grid-template-columns: 10px minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 18px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.94);
          box-shadow: 0 18px 42px rgba(2,6,23,0.14);
          backdrop-filter: blur(14px);
        }
        .csv2-floating-toast-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: rgba(56,104,255,0.95);
          box-shadow: 0 0 0 8px rgba(56,104,255,0.12);
        }
        .csv2-floating-toast-success .csv2-floating-toast-dot {
          background: rgba(16,185,129,1);
          box-shadow: 0 0 0 8px rgba(16,185,129,0.14);
        }
        .csv2-floating-toast-warning .csv2-floating-toast-dot {
          background: rgba(245,158,11,1);
          box-shadow: 0 0 0 8px rgba(245,158,11,0.14);
        }
        .csv2-floating-toast-danger .csv2-floating-toast-dot {
          background: rgba(239,68,68,1);
          box-shadow: 0 0 0 8px rgba(239,68,68,0.12);
        }
        .csv2-floating-toast-copy {
          min-width: 0;
          font-size: 0.82rem;
          font-weight: 600;
          line-height: 1.45;
          color: rgba(11,18,32,0.88);
        }
        .csv2-floating-toast-close {
          width: 30px;
          height: 30px;
          border: 0;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: rgba(11,18,32,0.64);
          background: rgba(148,163,184,0.14);
        }
        .csv2-confirm-bubble {
          position: fixed;
          z-index: 52;
          width: min(320px, calc(100vw - 28px));
          padding: 14px;
          border-radius: 18px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.96);
          box-shadow: 0 18px 42px rgba(2,6,23,0.16);
          backdrop-filter: blur(14px);
          transform: translateY(-100%);
        }
        .csv2-confirm-bubble.is-fallback {
          top: auto;
          left: auto;
          right: 18px;
          bottom: 18px;
          transform: none;
        }
        .csv2-confirm-bubble-kicker {
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(56,104,255,0.82);
        }
        .csv2-confirm-bubble-text {
          margin-top: 6px;
          font-size: 0.84rem;
          line-height: 1.5;
          color: rgba(11,18,32,0.9);
        }
        .csv2-confirm-bubble-actions {
          margin-top: 12px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .csv2-card {
          background: var(--wv-card);
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          color: inherit;
          box-shadow: 0 10px 26px rgba(2,6,23,0.06);
        }
        .csv2-topbar {
          display: grid;
          grid-template-columns: auto minmax(260px, 1fr) auto;
          gap: 12px;
          align-items: center;
          padding: 10px 14px;
          margin-bottom: 12px;
          background: rgba(255,255,255,0.88);
          backdrop-filter: blur(10px);
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          box-shadow: 0 10px 26px rgba(2,6,23,0.06);
        }
        .csv2-brand {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
        }
        .csv2-brand-mark {
          width: 34px;
          height: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }
        .csv2-brand-mark span {
          display: block;
          width: 10px;
          border-radius: 999px;
          background: linear-gradient(180deg, rgba(56,104,255,1), rgba(29,78,216,1));
        }
        .csv2-brand-mark span:first-child {
          height: 22px;
          transform: rotate(-28deg);
        }
        .csv2-brand-mark span:last-child {
          height: 16px;
          transform: rotate(-28deg);
          opacity: 0.82;
        }
        .csv2-brand-text {
          font-size: 1.12rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--wv-text);
        }
        .csv2-search {
          height: 44px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 14px;
          border-radius: 14px;
          background: rgba(255,255,255,0.78);
          border: 1px solid var(--wv-border);
        }
        .csv2-search i { color: rgba(11,18,32,0.42); }
        .csv2-search input {
          width: 100%;
          border: 0;
          outline: 0;
          background: transparent;
          font-size: 0.92rem;
          color: var(--wv-text);
        }
        .csv2-search input::placeholder,
        .csv2-upload-form input::placeholder,
        .csv2-upload-form textarea::placeholder {
          color: rgba(11,18,32,0.48);
        }
        .csv2-join-btn,
        .csv2-primary-btn,
        .csv2-soft-btn,
        .csv2-close {
          transition: background-color .18s ease, border-color .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .csv2-join-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 40px;
          padding: 0 16px;
          border-radius: 14px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.82);
          color: rgba(11,18,32,0.88);
          text-decoration: none;
          font-size: 0.84rem;
          font-weight: 700;
          box-shadow: 0 8px 18px rgba(2,6,23,0.04);
        }
        .csv2-join-btn:hover {
          background: rgba(255,255,255,0.96);
          border-color: rgba(15,23,42,0.14);
          color: rgba(11,18,32,0.95);
          text-decoration: none;
        }
        .csv2-primary-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 42px;
          padding: 0 16px;
          border: 1px solid rgba(56,104,255,0.26);
          border-radius: 14px;
          background: linear-gradient(180deg, rgba(56,104,255,0.96), rgba(37,99,235,0.96));
          color: #fff;
          font-size: 0.84rem;
          font-weight: 800;
          text-decoration: none;
          box-shadow: 0 12px 24px rgba(56,104,255,0.18);
        }
        .csv2-primary-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(56,104,255,0.22);
        }
        .csv2-primary-btn:disabled {
          opacity: 0.68;
          cursor: not-allowed;
          box-shadow: none;
          transform: none;
        }
        .csv2-soft-btn,
        .csv2-close {
          min-height: 40px;
          padding: 0 14px;
          border-radius: 14px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.86);
          color: rgba(11,18,32,0.86);
          font-size: 0.82rem;
          font-weight: 700;
          box-shadow: 0 8px 18px rgba(2,6,23,0.04);
        }
        .csv2-soft-btn:hover,
        .csv2-close:hover {
          background: rgba(56,104,255,0.06);
          border-color: rgba(56,104,255,0.24);
        }
        .csv2-main-grid {
          display: grid;
          grid-template-columns: clamp(290px, 24vw, 360px) minmax(560px, 1fr) clamp(280px, 22vw, 340px);
          gap: 12px;
          align-items: stretch;
        }
        .csv2-left-card,
        .csv2-right-card,
        .csv2-center-card {
          padding: 14px;
        }
        .csv2-left-card {
          display: flex;
          flex-direction: column;
          min-height: 640px;
        }
        .csv2-left-tabs {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .csv2-left-tabs button,
        .csv2-left-tabs a {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 34px;
          padding: 0 12px;
          border: 1px solid var(--wv-border);
          border-radius: 999px;
          background: rgba(255,255,255,0.70);
          color: rgba(11,18,32,0.86);
          font-size: 0.78rem;
          font-weight: 700;
          text-decoration: none;
        }
        .csv2-left-tabs .active {
          background: var(--wv-accent-soft);
          border-color: rgba(56,104,255,0.30);
          color: rgba(11,18,32,0.92);
        }
        .csv2-library-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .csv2-library-title-row {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.92rem;
          font-weight: 800;
          color: rgba(11,18,32,0.92);
        }
        .csv2-library-title-row i {
          color: rgba(107,114,128,0.95);
          font-size: 0.95rem;
        }
        .csv2-library-switch {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }
        .csv2-library-switch input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .csv2-library-switch-track {
          position: relative;
          width: 44px;
          height: 24px;
          border-radius: 999px;
          background: rgba(203,213,225,0.92);
          box-shadow: inset 0 0 0 1px rgba(148,163,184,0.34);
          transition: background-color .18s ease;
          flex: 0 0 auto;
        }
        .csv2-library-switch-track::after {
          content: '';
          position: absolute;
          top: 3px;
          left: 3px;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #fff;
          box-shadow: 0 2px 6px rgba(15,23,42,0.18);
          transition: transform .18s ease;
        }
        .csv2-library-switch input:checked + .csv2-library-switch-track {
          background: rgba(37,99,235,0.96);
        }
        .csv2-library-switch input:checked + .csv2-library-switch-track::after {
          transform: translateX(20px);
        }
        .csv2-library-switch-label {
          font-size: 0.78rem;
          font-weight: 700;
          color: rgba(11,18,32,0.84);
        }

        .csv2-left-search {
          height: 40px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 12px;
          border-radius: 14px;
          background: rgba(255,255,255,0.78);
          border: 1px solid var(--wv-border);
          margin-bottom: 10px;
        }
        .csv2-left-search i {
          color: rgba(11,18,32,0.42);
        }
        .csv2-left-search input {
          width: 100%;
          border: 0;
          outline: 0;
          background: transparent;
          font-size: 0.84rem;
          color: var(--wv-text);
        }
        .csv2-library-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .csv2-library-toolbar-right {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .csv2-library-meta {
          font-size: 0.76rem;
          color: var(--wv-muted);
          font-weight: 700;
        }
        .csv2-library-reset {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.82);
          color: rgba(11,18,32,0.70);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 18px rgba(2,6,23,0.04);
        }
        .csv2-library-select {
          min-height: 36px;
          padding: 0 12px;
          border-radius: 12px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.78);
          color: rgba(11,18,32,0.86);
          font-size: 0.78rem;
          font-weight: 700;
          min-width: 132px;
        }
        .csv2-library-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          overflow: auto;
          min-height: 0;
          max-height: calc(100vh - 320px);
          padding: 2px 2px 2px 0;
          align-content: start;
        }
        .csv2-library-grid.is-disabled {
          opacity: 0.8;
        }
        .csv2-library-item {
          display: flex;
          flex-direction: column;
          min-height: 154px;
          border-radius: 14px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.96);
          overflow: hidden;
          box-shadow: 0 8px 18px rgba(2,6,23,0.04);
        }
        .csv2-library-thumb {
          position: relative;
          width: 100%;
          flex: 0 0 92px;
          height: 92px;
          overflow: hidden;
          background: rgba(15,23,42,0.04);
        }
        .csv2-library-thumb img,
        .csv2-library-thumb video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .csv2-library-thumb-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px;
          background: linear-gradient(180deg, rgba(241,245,249,0.92), rgba(226,232,240,0.92));
          color: rgba(71,85,105,0.95);
          text-align: center;
          font-size: 0.72rem;
          font-weight: 700;
        }
        .csv2-library-thumb-fallback i {
          font-size: 1.1rem;
          color: rgba(29,78,216,0.95);
        }
        .csv2-library-video-badge {
          position: absolute;
          left: 10px;
          bottom: 10px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 24px;
          padding: 0 8px;
          border-radius: 999px;
          background: rgba(15,23,42,0.72);
          color: #fff;
          font-size: 0.68rem;
          font-weight: 800;
          box-shadow: 0 6px 14px rgba(2,6,23,0.18);
        }
        .csv2-library-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          min-height: 48px;
          padding: 8px;
        }
        .csv2-library-copy {
          flex: 1 1 auto;
          min-width: 0;
        }
        .csv2-library-title {
          font-size: 0.72rem;
          font-weight: 700;
          color: rgba(11,18,32,0.88);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .csv2-library-sub {
          margin-top: 1px;
          font-size: 0.64rem;
          color: var(--wv-muted);
        }
        .csv2-library-add {
          flex: 0 0 auto;
          width: 28px;
          height: 28px;
          border-radius: 9px;
          border: 1px solid rgba(56,104,255,0.26);
          background: rgba(56,104,255,0.08);
          color: rgba(29,78,216,1);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 14px rgba(56,104,255,0.10);
          font-size: 0.76rem;
        }
        .csv2-library-add:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          box-shadow: none;
        }
        .csv2-left-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          overflow: auto;
          min-height: 0;
          max-height: calc(100vh - 290px);
          padding-right: 2px;
        }
        .csv2-contribution-row {
          width: 100%;
          display: grid;
          grid-template-columns: 96px minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
          padding: 10px;
          border-radius: 16px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.78);
          text-align: left;
          cursor: pointer;
        }
        .csv2-contribution-row:hover {
          background: rgba(255,255,255,0.96);
          border-color: rgba(15,23,42,0.14);
        }
        .csv2-contribution-row.is-active {
          outline: 2px solid rgba(56,104,255,0.32);
          border-color: rgba(56,104,255,0.22);
          background: rgba(56,104,255,0.06);
        }
        .csv2-contribution-thumb {
          position: relative;
          width: 96px;
          height: 72px;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(15,23,42,0.10);
          background: rgba(15,23,42,0.03);
        }
        .csv2-thumb-video-badge {
          position: absolute;
          right: 6px;
          bottom: 6px;
          width: 20px;
          height: 20px;
          border-radius: 999px;
          background: rgba(15,23,42,0.72);
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.66rem;
          box-shadow: 0 6px 14px rgba(2,6,23,0.18);
        }
        .csv2-contribution-thumb img,
        .csv2-contribution-thumb video,
        .csv2-slot-thumb img,
        .csv2-slot-thumb video,
        .csv2-stage-media img,
        .csv2-stage-media video,
        .csv2-editor-thumb img,
        .csv2-editor-thumb video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .csv2-contribution-copy {
          min-width: 0;
        }
        .csv2-contribution-title {
          margin: 0;
          font-size: 0.86rem;
          line-height: 1.15;
          font-weight: 700;
          color: rgba(11,18,32,0.92);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .csv2-contribution-meta {
          display: flex;
          gap: 6px;
          align-items: center;
          margin-top: 3px;
          font-size: 0.72rem;
          color: var(--wv-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .csv2-status {
          margin-top: 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 24px;
          padding: 0 8px;
          border-radius: 999px;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          font-size: 0.70rem;
          font-weight: 800;
          white-space: nowrap;
        }
        .csv2-status-success {
          border-color: rgba(16,185,129,0.25);
          background: var(--wv-green-soft);
          color: rgba(6,95,70,0.95);
        }
        .csv2-status-info {
          border-color: rgba(56,104,255,0.22);
          background: var(--wv-accent-soft);
          color: rgba(30,64,175,0.95);
        }
        .csv2-status-muted {
          color: rgba(11,18,32,0.62);
        }
        .csv2-contribution-side {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .csv2-contribution-side img,
        .csv2-message-card img {
          width: 34px;
          height: 34px;
          border-radius: 999px;
          object-fit: cover;
          border: 1px solid rgba(15,23,42,0.14);
          background: rgba(255,255,255,0.92);
          box-shadow: 0 8px 18px rgba(2,6,23,0.05);
        }
        .csv2-delete {
          width: 22px;
          height: 22px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(239,68,68,0.14);
          background: var(--wv-red-soft);
          color: rgba(153,27,27,0.95);
          font-size: 0.72rem;
        }
        .csv2-empty {
          display: grid;
          place-items: center;
          min-height: 180px;
          border-radius: 16px;
          border: 1px dashed rgba(15,23,42,0.14);
          background: rgba(255,255,255,0.46);
          color: var(--wv-muted);
          font-size: 0.84rem;
          font-weight: 700;
        }
        .csv2-center-card {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .csv2-center-head {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
          padding-bottom: 10px;
          border-bottom: 1px solid var(--wv-border);
          flex-wrap: wrap;
        }
        .csv2-kicker,
        .csv2-editor-kicker {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-weight: 800;
          color: rgba(11,18,32,0.46);
        }
        .csv2-center-head h2 {
          margin: 4px 0 4px;
          font-size: clamp(1.2rem, 1.65vw, 1.55rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--wv-text);
        }
        .csv2-subline {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          color: var(--wv-muted);
          font-size: 0.82rem;
        }
        .csv2-owner-studio-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 32px;
          padding: 0 16px;
          border: 1px solid rgba(56,104,255,0.26);
          border-radius: 14px;
          background: linear-gradient(180deg, rgba(56,104,255,0.96), rgba(37,99,235,0.96));
          color: #fff;
          font-size: 0.84rem;
          font-weight: 700;
          text-decoration: none;
          white-space: nowrap;
          box-shadow: 0 12px 24px rgba(56,104,255,0.18);
          transition: background-color .18s ease, border-color .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .csv2-owner-studio-link:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(56,104,255,0.22);
          color: #fff;
          text-decoration: none;
        }
        .csv2-role-badge {
          display: inline-flex;
          align-items: center;
          min-height: 24px;
          padding: 0 8px;
          border-radius: 999px;
          border: 1px solid rgba(56,104,255,0.22);
          background: var(--wv-accent-soft);
          color: rgba(30,64,175,0.95);
          font-size: 0.72rem;
          font-weight: 800;
          text-transform: capitalize;
        }
        .csv2-preview-stage {
          padding: 10px;
          border-radius: 18px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.72);
        }
        .csv2-stage-media {
          width: 100%;
          height: clamp(240px, 38vh, 460px);
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
        }
        .csv2-stage-media video {
          object-fit: contain;
          background: rgba(15,23,42,0.03);
        }
        .csv2-stage-empty,
        .csv2-editor-thumb-empty,
        .csv2-slot-empty {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 10px;
          color: var(--wv-muted);
          font-weight: 700;
          background: rgba(15,23,42,0.03);
        }
        .csv2-stage-empty i,
        .csv2-editor-thumb-empty i,
        .csv2-slot-empty i {
          font-size: 1.2rem;
          color: rgba(56,104,255,0.92);
        }
        .csv2-stage-toolbar {
          margin-top: 10px;
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 12px;
          align-items: center;
          color: var(--wv-muted);
          font-size: 0.78rem;
        }
        .csv2-stage-actions {
          display: inline-flex;
          gap: 10px;
          font-size: 0.9rem;
        }
        .csv2-stage-progress,
        .csv2-progress-ghost {
          height: 6px;
          border-radius: 999px;
          background: rgba(15,23,42,0.08);
          overflow: hidden;
        }
        .csv2-stage-progress span,
        .csv2-progress-ghost span {
          display: block;
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, rgba(56,104,255,1), rgba(16,185,129,1));
        }
        .csv2-stage-time {
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.08em;
        }
        .csv2-editor-zone {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 240px;
          gap: 14px;
          align-items: stretch;
          border-radius: 18px;
          border: 1px solid var(--wv-border);
          padding: 14px;
          background: rgba(255,255,255,0.70);
        }
        .csv2-editor-title {
          margin-top: 6px;
          font-size: 1.06rem;
          font-weight: 600;
          color: var(--wv-text);
        }
        .csv2-editor-subtitle {
          margin-top: 4px;
          font-size: 0.84rem;
          color: var(--wv-muted);
        }
        .csv2-editor-inline {
          margin-top: 14px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .csv2-editor-thumb {
          width: 188px;
          height: 112px;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          flex-shrink: 0;
        }
        .csv2-inline-btn {
          min-height: 44px;
        }
        .csv2-suggestions,
        .csv2-message-card,
        .csv2-metric-box {
          background: rgba(255,255,255,0.76);
          border: 1px solid var(--wv-border);
        }
        .csv2-suggestions {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px;
          border-radius: 16px;
        }
        .csv2-suggestions-title,
        .csv2-block-title {
          font-size: 0.86rem;
          font-weight: 800;
          color: rgba(11,18,32,0.86);
        }
        .csv2-suggestion-row {
          display: flex;
          gap: 8px;
          align-items: flex-start;
          font-size: 0.78rem;
          color: rgba(11,18,32,0.78);
          line-height: 1.45;
        }
        .csv2-suggestion-row i {
          color: rgba(16,185,129,1);
          margin-top: 2px;
        }
        .csv2-mini-track {
          position: relative;
          border-radius: 999px;
          background: repeating-linear-gradient(
            90deg,
            rgba(148,163,184,0.18) 0 2px,
            transparent 2px 9px
          );
        }
        .csv2-mini-track span {
          position: absolute;
          top: -2px;
          width: 4px;
          height: 22px;
          border-radius: 999px;
          background: linear-gradient(180deg, rgba(56,104,255,1), rgba(37,99,235,1));
          box-shadow: 0 0 0 4px rgba(56,104,255,0.10);
        }
        .csv2-right-card {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .csv2-info-block {
          padding-bottom: 12px;
          border-bottom: 1px solid var(--wv-border);
        }
        .csv2-info-block:last-child {
          border-bottom: 0;
          padding-bottom: 0;
        }
        .csv2-message-card {
          margin-top: 10px;
          padding: 12px;
          border-radius: 16px;
          display: grid;
          grid-template-columns: 34px minmax(0, 1fr);
          gap: 10px;
          align-items: start;
        }
        .csv2-message-title {
          font-size: 0.78rem;
          font-weight: 800;
          color: rgba(11,18,32,0.92);
        }
        .csv2-message-text {
          margin-top: 2px;
          font-size: 0.76rem;
          color: var(--wv-muted);
          line-height: 1.45;
        }
        .csv2-metric-box {
          margin-top: 10px;
          padding: 12px;
          border-radius: 16px;
        }
        .csv2-metric-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          font-size: 0.78rem;
          font-weight: 700;
          color: rgba(11,18,32,0.82);
          margin-bottom: 10px;
        }
        .csv2-metric-box input[type="range"] {
          width: 100%;
        }
        .csv2-coin-sub {
          font-size: 0.76rem;
          color: var(--wv-muted);
        }
        .csv2-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
        }
        .csv2-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          font-size: 0.72rem;
          font-weight: 800;
        }
        .csv2-pill.blue {
          border-color: rgba(56,104,255,0.20);
          background: var(--wv-accent-soft);
          color: rgba(30,64,175,0.95);
        }
        .csv2-pill.green {
          border-color: rgba(16,185,129,0.25);
          background: var(--wv-green-soft);
          color: rgba(6,95,70,0.95);
        }
        .csv2-pill.light {
          color: rgba(11,18,32,0.72);
        }
        .csv2-slotline-card {
          position: sticky;
          bottom: 12px;
          z-index: 20;
          margin-top: 12px;
          padding: 12px;
          background: rgba(246,247,251,0.72);
          backdrop-filter: blur(10px);
          border-top: 1px solid var(--wv-border);
          box-shadow: 0 -10px 26px rgba(2,6,23,0.06);
        }
        .csv2-slotline-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }
        .csv2-slotline-head h3 {
          margin: 0;
          font-size: 0.98rem;
          font-weight: 600;
          color: var(--wv-text);
        }
        .csv2-slotline-sub {
          margin-top: 4px;
          color: var(--wv-muted);
          font-size: 0.76rem;
        }
        .csv2-slotline-scroll {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 6px;
        }
        .csv2-slot-card {
          flex: 0 0 190px;
          max-width: 190px;
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          padding: 10px;
          background: rgba(255,255,255,0.80);
          text-align: left;
          cursor: pointer;
        }
        .csv2-slot-card:hover {
          background: rgba(255,255,255,0.96);
        }
        .csv2-slot-card.is-active {
          outline: 2px solid rgba(56,104,255,0.32);
          background: rgba(56,104,255,0.06);
          border-color: rgba(56,104,255,0.22);
        }
        .csv2-slot-thumb {
          width: 100%;
          height: 92px;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
        }
        .csv2-slot-copy {
          margin-top: 8px;
        }
        .csv2-slot-title {
          margin: 0;
          font-size: 0.78rem;
          font-weight: 700;
          color: rgba(11,18,32,0.86);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .csv2-slot-meta {
          margin-top: 2px;
          font-size: 0.72rem;
          color: var(--wv-muted);
        }
        .csv2-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 1080;
          display: grid;
          place-items: center;
          padding: 20px;
          background: rgba(2,6,23,0.42);
          backdrop-filter: blur(8px);
        }
        .csv2-modal-card {
          width: min(680px, 100%);
          padding: 16px;
          border-radius: 18px;
          background: var(--wv-card);
          border: 1px solid var(--wv-border);
          box-shadow: 0 18px 50px rgba(2,6,23,0.28);
        }
        .csv2-modal-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 14px;
        }
        .csv2-modal-head h3 {
          margin: 4px 0 0;
          font-size: 1.06rem;
          font-weight: 800;
          color: var(--wv-text);
        }
        .csv2-close {
          width: 40px;
          height: 40px;
          padding: 0;
        }
        .csv2-upload-form {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .csv2-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }
        .csv2-upload-form label {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .csv2-upload-form span {
          font-size: 0.78rem;
          font-weight: 700;
          color: rgba(11,18,32,0.76);
        }
        .csv2-upload-form input,
        .csv2-upload-form textarea {
          width: 100%;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.14);
          background: rgba(255,255,255,0.90);
          outline: 0;
          padding: 12px 14px;
          color: rgba(11,18,32,0.92);
          box-shadow: none;
        }
        .csv2-upload-form input:focus,
        .csv2-upload-form textarea:focus,
        .csv2-search input:focus {
          outline: none;
        }
        .csv2-upload-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 4px;
          flex-wrap: wrap;
        }
        @media (max-width: 1400px) {
          .csv2-main-grid {
            grid-template-columns: 290px minmax(420px, 1fr) 320px;
            gap: 10px;
          }
          .csv2-contribution-row {
            grid-template-columns: 88px minmax(0, 1fr) auto;
          }
          .csv2-contribution-thumb {
            width: 88px;
            height: 68px;
          }
        }
        @media (max-width: 1280px) {
          .csv2-main-grid {
            grid-template-columns: minmax(260px, 300px) minmax(0, 1fr);
          }
          .csv2-right-card {
            grid-column: 1 / -1;
          }
          .csv2-editor-zone {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 1100px) {
          .wv-studio-v2.csv2-wrap {
            padding-left: 10px;
            padding-right: 10px;
          }
          .csv2-topbar,
          .csv2-main-grid {
            grid-template-columns: 1fr;
          }
          .csv2-left-card,
          .csv2-center-card,
          .csv2-right-card {
            min-height: auto;
          }
          .csv2-left-list {
            max-height: none;
          }
          .csv2-slotline-card {
            position: static;
          }
        }
        @media (max-width: 760px) {
          .wv-studio-v2.csv2-wrap {
            padding: calc(var(--wv-app-top-offset) + 8px) 8px 24px;
          }
          .csv2-card,
          .csv2-topbar,
          .csv2-modal-card,
          .csv2-slotline-card {
            border-radius: 16px;
          }
          .csv2-center-head,
          .csv2-editor-inline,
          .csv2-upload-actions,
          .csv2-slotline-head {
            flex-direction: column;
            align-items: stretch;
          }
          .csv2-form-grid {
            grid-template-columns: 1fr;
          }
          .csv2-contribution-row {
            grid-template-columns: 72px minmax(0, 1fr);
          }
          .csv2-contribution-thumb {
            width: 72px;
            height: 58px;
          }
          .csv2-contribution-side {
            display: none;
          }
          .csv2-editor-thumb,
          .csv2-primary-btn,
          .csv2-join-btn,
          .csv2-soft-btn {
            width: 100%;
          }
          .csv2-slot-card {
            flex-basis: 160px;
            max-width: 160px;
          }
        }
      

        /* ===== JourneyStudioV2 visual parity (style only, no layout changes) ===== */
        .wv-studio-v2.csv2-wrap {
          --wv-app-left-offset: 0px;
          --wv-shell-top: calc(var(--wv-app-top-offset, 72px) + 10px);
        }

        .csv2-card,
        .csv2-topbar,
        .csv2-slotline-card,
        .csv2-modal-card,
        .csv2-preview-stage,
        .csv2-editor-zone,
        .csv2-suggestions,
        .csv2-message-card,
        .csv2-metric-box,
        .csv2-library-item {
          background: var(--wv-card);
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          box-shadow: 0 10px 26px rgba(2,6,23,0.06);
        }

        .csv2-left-card,
        .csv2-center-card,
        .csv2-right-card,
        .csv2-editor-zone,
        .csv2-suggestions {
          padding: 14px;
        }

        .csv2-topbar {
          padding: 10px 14px;
          background: rgba(255,255,255,0.88);
          backdrop-filter: blur(10px);
        }

        .csv2-center-head {
          padding-bottom: 10px;
        }

        .csv2-center-head h2,
        .csv2-editor-title,
        .csv2-modal-head h3 {
          letter-spacing: 0.2px;
          color: var(--wv-text);
        }

        .csv2-center-head h2 {
          font-size: clamp(1.05rem, 1.45vw, 1.32rem);
          font-weight: 700;
          margin: 4px 0;
        }

        .csv2-editor-title {
          font-size: 0.98rem;
          font-weight: 800;
          margin-top: 4px;
        }

        .csv2-subline,
        .csv2-editor-subtitle,
        .csv2-stage-toolbar,
        .csv2-suggestion-row,
        .csv2-message-text,
        .csv2-library-sub,
        .csv2-contribution-meta,
        .csv2-coin-sub {
          font-size: 0.78rem;
          line-height: 1.45;
          color: var(--wv-muted);
        }

        .csv2-kicker,
        .csv2-editor-kicker,
        .csv2-stage-time,
        .csv2-library-meta,
        .csv2-pill,
        .csv2-status,
        .csv2-role-badge {
          letter-spacing: 0.04em;
        }

        .csv2-kicker,
        .csv2-editor-kicker {
          font-size: 0.72rem;
          font-weight: 700;
          color: rgba(11,18,32,0.58);
        }

        .csv2-left-tabs button,
        .csv2-left-tabs a,
        .csv2-soft-btn,
        .csv2-close,
        .csv2-primary-btn,
        .csv2-join-btn,
        .csv2-library-reset,
        .csv2-library-select,
        .csv2-library-add,
        .csv2-delete {
          font-style: normal;
          letter-spacing: 0.2px;
        }

        .csv2-left-tabs button,
        .csv2-left-tabs a {
          min-height: 34px;
          padding: 0 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.72);
          border: 1px solid var(--wv-border);
          color: rgba(11,18,32,0.86);
          font-size: 0.86rem;
          font-weight: 700;
        }

        .csv2-left-tabs .active {
          background: rgba(56,104,255,0.14);
          border-color: rgba(56,104,255,0.30);
          color: rgba(11,18,32,0.92);
        }

        .csv2-primary-btn,
        .csv2-join-btn,
        .csv2-soft-btn,
        .csv2-close {
          min-height: 40px;
          padding: 0 14px;
          border-radius: 14px;
          font-size: 0.82rem;
          box-shadow: 0 8px 18px rgba(2,6,23,0.04);
        }

        .csv2-primary-btn,
        .csv2-join-btn {
          font-weight: 600;
        }

        .csv2-soft-btn,
        .csv2-close,
        .csv2-library-reset,
        .csv2-library-select,
        .csv2-library-add {
          border-radius: 14px;
          box-shadow: 0 8px 18px rgba(2,6,23,0.04);
        }

        .csv2-soft-btn,
        .csv2-close,
        .csv2-library-select {
          font-size: 0.82rem;
          font-weight: 700;
        }

        .csv2-library-reset,
        .csv2-library-add,
        .csv2-delete {
          border-radius: 12px;
        }

        .csv2-left-search,
        .csv2-upload-form input:not([type="checkbox"]):not([type="range"]):not([type="file"]),
        .csv2-upload-form textarea,
        .csv2-library-select {
          border-radius: 14px;
          border-color: rgba(15,23,42,0.14);
          background: rgba(255,255,255,0.90);
          color: rgba(11,18,32,0.92);
        }

        .csv2-left-search {
          height: 40px;
          padding: 0 12px;
        }

        .csv2-left-search input,
        .csv2-upload-form input:not([type="checkbox"]):not([type="range"]):not([type="file"]),
        .csv2-upload-form textarea,
        .csv2-library-select {
          font-size: 0.84rem;
        }

        .csv2-left-search input::placeholder,
        .csv2-upload-form input::placeholder,
        .csv2-upload-form textarea::placeholder {
          color: rgba(11,18,32,0.50);
        }

        .csv2-contribution-row,
        .csv2-slot-card {
          border-radius: 16px;
        }

        .csv2-contribution-thumb,
        .csv2-editor-thumb,
        .csv2-slot-thumb,
        .csv2-stage-media {
          border-radius: 14px;
        }

        .csv2-contribution-title,
        .csv2-suggestions-title,
        .csv2-block-title,
        .csv2-message-title,
        .csv2-library-title {
          color: rgba(11,18,32,0.86);
        }

        .csv2-contribution-title,
        .csv2-suggestions-title,
        .csv2-block-title {
          font-size: 0.84rem;
          font-weight: 700;
        }

        .csv2-message-title {
          font-size: 0.78rem;
          font-weight: 800;
        }

        .csv2-library-title-row {
          font-size: 0.9rem;
          font-weight: 800;
        }

        .csv2-library-switch-label,
        .csv2-library-meta {
          font-size: 0.76rem;
          font-weight: 700;
          color: rgba(11,18,32,0.78);
        }

        .csv2-pill,
        .csv2-status,
        .csv2-role-badge {
          font-size: 0.72rem;
          font-weight: 800;
        }

        .csv2-empty,
        .csv2-stage-empty,
        .csv2-editor-thumb-empty,
        .csv2-slot-empty {
          color: var(--wv-muted);
        }


        .csv2-library-head-actions {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .csv2-inline-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .csv2-inline-btn i,
        .csv2-slotline-add i,
        .csv2-ai-actions-row i,
        .csv2-pill i {
          line-height: 1;
        }
        .csv2-editor-zone-rich {
          grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
          gap: 16px;
          align-items: start;
          background: linear-gradient(180deg, rgba(255,255,255,0.78), rgba(255,255,255,0.66));
        }
        .csv2-editor-inline-rich {
          align-items: stretch;
        }
        .csv2-editor-thumb-rich {
          width: 220px;
          height: 132px;
        }
        .csv2-editor-stack {
          display: grid;
          gap: 12px;
          flex: 1 1 260px;
          min-width: 0;
        }
        .csv2-inline-metrics {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }
        .csv2-inline-metric {
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.82);
          border-radius: 16px;
          padding: 12px;
          display: grid;
          gap: 6px;
        }
        .csv2-inline-metric-hero {
          background: linear-gradient(180deg, rgba(56,104,255,0.08), rgba(56,104,255,0.03));
          border-color: rgba(56,104,255,0.16);
        }
        .csv2-inline-label {
          font-size: 0.7rem;
          line-height: 1.35;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-weight: 800;
          color: rgba(11,18,32,0.54);
        }
        .csv2-inline-metric strong,
        .csv2-slot-accept-rate,
        .csv2-ai-hero-title {
          font-size: 1rem;
          font-weight: 800;
          color: rgba(11,18,32,0.92);
        }
        .csv2-inline-metric small,
        .csv2-ai-panel-sub,
        .csv2-right-sub,
        .csv2-slot-accept-copy span,
        .csv2-rank-copy small,
        .csv2-ai-hero-sub {
          font-size: 0.75rem;
          line-height: 1.45;
          color: var(--wv-muted);
        }
        .csv2-inline-status-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .csv2-progress-tight {
          height: 7px;
          margin-top: 2px;
        }
        .csv2-ai-summary-card,
        .csv2-ai-reference-card,
        .csv2-rank-item,
        .csv2-slot-accept-row {
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.82);
          border-radius: 16px;
        }
        .csv2-ai-summary-card,
        .csv2-ai-reference-card {
          padding: 12px;
        }
        .csv2-ai-summary-copy {
          margin-top: 8px;
          font-size: 0.8rem;
          line-height: 1.5;
          color: rgba(11,18,32,0.80);
        }
        .csv2-ai-summary-tags {
          margin-top: 10px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .csv2-ai-review-panel {
          gap: 12px;
          background: linear-gradient(180deg, rgba(56,104,255,0.06), rgba(255,255,255,0.86));
          border-color: rgba(56,104,255,0.14);
        }
        .csv2-slot-coach-panel {
          gap: 12px;
        }
        .csv2-slot-coach-summary {
          display: grid;
          grid-template-columns: minmax(0, 1.2fr) minmax(220px, 0.8fr);
          gap: 12px;
        }
        .csv2-slot-coach-context,
        .csv2-slot-coach-compact,
        .csv2-slot-coach-hints {
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 16px;
          background: rgba(255,255,255,0.8);
          padding: 12px;
        }
        .csv2-slot-coach-context {
          display: grid;
          gap: 6px;
        }
        .csv2-slot-coach-context strong,
        .csv2-slot-pulse-copy strong {
          font-size: 0.92rem;
          color: rgba(11,18,32,0.92);
        }
        .csv2-slot-coach-context > span:last-child,
        .csv2-slot-pulse-copy > span:last-child {
          font-size: 0.77rem;
          line-height: 1.5;
          color: var(--wv-muted);
        }
        .csv2-slot-coach-kicker,
        .csv2-slot-coach-compact-title {
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(56,104,255,0.82);
        }
        .csv2-slot-coach-score {
          min-height: 100%;
        }
        .csv2-slot-coach-notes {
          background: rgba(255,255,255,0.78);
        }
        .csv2-slot-coach-compact-grid {
          margin-top: 10px;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        .csv2-slot-coach-chip {
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.06);
          background: rgba(246,247,251,0.88);
          padding: 10px;
          display: grid;
          gap: 4px;
        }
        .csv2-slot-coach-chip span {
          font-size: 0.69rem;
          color: var(--wv-muted);
        }
        .csv2-slot-coach-chip strong {
          font-size: 0.88rem;
          color: rgba(11,18,32,0.92);
        }
        .csv2-slot-coach-toggle {
          align-self: flex-start;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .csv2-slot-pulse-hero {
          margin-top: 10px;
          display: grid;
          grid-template-columns: 86px minmax(0, 1fr);
          gap: 12px;
          align-items: center;
          padding: 12px;
          border-radius: 16px;
          border: 1px solid rgba(56,104,255,0.12);
          background: rgba(255,255,255,0.84);
        }
        .csv2-slot-pulse-core {
          position: relative;
          width: 74px;
          height: 74px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          background: radial-gradient(circle at center, rgba(56,104,255,0.12), rgba(56,104,255,0.02));
          color: rgba(56,104,255,0.96);
          font-size: 0.96rem;
          font-weight: 800;
        }
        .csv2-slot-pulse-dot {
          position: absolute;
          inset: 8px;
          border-radius: inherit;
          border: 2px solid rgba(56,104,255,0.18);
          animation: csv2Pulse 1.8s ease-in-out infinite;
        }
        .csv2-slot-pulse-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
        }
        .csv2-slot-accept-pill {
          align-self: flex-start;
          margin-top: -4px;
          padding: 4px 8px;
          border-radius: 999px;
          border: 1px solid rgba(56,104,255,0.14);
          background: rgba(56,104,255,0.08);
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: rgba(56,104,255,0.88);
        }
        @keyframes csv2Pulse {
          0%, 100% {
            transform: scale(0.9);
            opacity: 0.55;
          }
          50% {
            transform: scale(1.05);
            opacity: 1;
          }
        }
        .csv2-suggestions-head {
          display: flex;
          align-items: start;
          justify-content: space-between;
          gap: 10px;
        }
        .csv2-compact-btn {
          min-height: 34px;
          padding: 0 12px;
          font-size: 0.76rem;
        }
        .csv2-ai-review-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
        }
        .csv2-ai-review-hero,
        .csv2-ai-review-notes {
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 16px;
          background: rgba(255,255,255,0.78);
          padding: 12px;
        }
        .csv2-ai-review-hero {
          display: grid;
          grid-template-columns: 82px minmax(0, 1fr);
          gap: 12px;
          align-items: center;
        }
        .csv2-ai-ring {
          position: relative;
          width: 72px;
          height: 72px;
          display: grid;
          place-items: center;
          flex: 0 0 auto;
        }
        .csv2-ai-ring svg {
          position: absolute;
          inset: 0;
          transform: rotate(-90deg);
        }
        .csv2-ai-ring-track,
        .csv2-ai-ring-value {
          fill: none;
          stroke-width: 4;
          stroke-linecap: round;
        }
        .csv2-ai-ring-track {
          stroke: rgba(148,163,184,0.28);
        }
        .csv2-ai-ring-value {
          stroke: rgba(56,104,255,0.95);
        }
        .csv2-ai-ring strong {
          position: relative;
          z-index: 1;
          font-size: 0.9rem;
          font-weight: 800;
        }
        .csv2-ai-actions-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .csv2-ai-reference-card img {
          margin-top: 10px;
          display: block;
          width: 100%;
          max-height: 180px;
          object-fit: cover;
          border-radius: 14px;
          border: 1px solid var(--wv-border);
        }
        .csv2-right-card-rich {
          gap: 14px;
        }
        .csv2-info-block-featured {
          padding: 14px;
          margin: -2px;
          border: 1px solid rgba(56,104,255,0.14);
          border-radius: 18px;
          background: linear-gradient(180deg, rgba(56,104,255,0.05), rgba(255,255,255,0.88));
        }
        .csv2-metric-box-hero {
          background: rgba(255,255,255,0.86);
          border-color: rgba(56,104,255,0.12);
        }
        .csv2-slot-acceptance-list,
        .csv2-rank-list {
          margin-top: 10px;
          display: grid;
          gap: 8px;
        }
        .csv2-slot-accept-row,
        .csv2-rank-item {
          width: 100%;
          padding: 10px 12px;
          text-align: left;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
        }
        .csv2-slot-accept-row.is-active,
        .csv2-rank-item.is-active {
          border-color: rgba(56,104,255,0.22);
          background: rgba(56,104,255,0.08);
        }
        .csv2-slot-accept-copy,
        .csv2-rank-copy {
          min-width: 0;
          display: grid;
          gap: 2px;
        }
        .csv2-slot-accept-copy strong,
        .csv2-rank-copy strong {
          font-size: 0.8rem;
          color: rgba(11,18,32,0.90);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .csv2-slot-accept-rate {
          min-width: 54px;
          text-align: right;
          font-size: 0.9rem;
        }
        .csv2-rank-item {
          grid-template-columns: 44px minmax(0, 1fr);
        }
        .csv2-rank-score {
          width: 32px;
          height: 32px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          background: rgba(56,104,255,0.10);
          color: rgba(30,64,175,0.96);
          font-size: 0.76rem;
          font-weight: 800;
        }
        .csv2-pill.warn {
          border-color: rgba(245,158,11,0.26);
          background: rgba(245,158,11,0.12);
          color: rgba(146,64,14,0.95);
        }
        .csv2-slotline-add {
          min-height: 38px;
          padding: 0 14px;
        }
        @media (max-width: 1399px) {
          .csv2-inline-metrics {
            grid-template-columns: 1fr;
          }
          .csv2-editor-thumb-rich {
            width: 196px;
            height: 118px;
          }
        }
        @media (max-width: 1199px) {
          .csv2-editor-zone-rich,
          .csv2-ai-review-grid {
            grid-template-columns: 1fr;
          }
          .csv2-editor-thumb-rich {
            width: 100%;
            max-width: 260px;
          }
        }

        /* ===== End JourneyStudioV2 visual parity ===== */

        @media (max-width: 860px) {
          .csv2-floating-toast {
            left: 14px;
            right: 14px;
            min-width: auto;
            max-width: none;
          }
          .csv2-slot-coach-summary,
          .csv2-slot-coach-compact-grid,
          .csv2-slot-pulse-hero {
            grid-template-columns: 1fr;
          }
          .csv2-slot-pulse-core {
            width: 68px;
            height: 68px;
          }
          .csv2-confirm-bubble:not(.is-fallback) {
            right: 14px;
            left: 14px !important;
            top: auto !important;
            bottom: 14px;
            width: auto;
            transform: none;
          }
        }
`}</style>
    </div>
  );
}
